/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  atualizarDepartamento,
  baixarExcelDoFluxo,
  getCobertura,
  getEsteira,
  getExecucaoDetalhe,
  getExecucaoFilaPendentes,
  getExecucaoNano,
  getExecucoesAtivas,
  getGrupoBoard,
  getMovimento,
  getResumo,
  listarDepartamentos,
  listarGrupos,
  listarVinculosBots,
  IAreaCobertura,
  IBotBoard,
  IContagens,
  IEmpresaInfo,
  IEmpresaResumo,
  IFatiaCobertura,
  IGrupoEsteira,
  IMicroCobertura,
  IModuloNano,
  StatusEsteira,
  STALE_TEMPO_REAL,
} from '../api';
import { C, FONTE_MONO } from '../paleta';
import {
  ehGrupoReal,
  emojiMeta,
  GrupoSelecionado,
  pctSucesso,
  SEM_GRUPO_ID,
  TODOS_GRUPOS_ID,
} from '../utils/ordemStatus';
import {
  empresasDoBotComStatus,
  empresasDoGrupoComStatus,
  IEmpresaComStatus,
  montarLinhasBots,
  nomeEmpresa,
  nomeModulo,
} from '../utils/derivarProcessos';
import { contagensDaLista } from '../utils/derivarResumo';
import {
  filtrarPorRecorte,
  jaPassaramPeloModulo,
  marcarForaDoResumo,
  ocupacaoEmVoo,
} from '../utils/derivarNano';
import {
  aoAlternarStatus,
  aoEscolherEmpresa,
  aoEscolherRegime,
  ComandoProcessos,
  empresasComLog,
  filtrarPorStatus,
  FiltrosCompartilhados,
  limparFiltros,
  regimesDisponiveis,
  statusPermitido,
  temFiltroAtivo,
} from '../utils/filtrosCompartilhados';
import { paraParams } from '../utils/periodoProcessos';
import { naoChegaramAoBot, naoChegaramAoGrupo } from '../utils/naoPassaram';
import { parcelasDoNo, type ParcelasDoNo } from '../utils/parcelasDoNo';
import type { NaoChegaram } from '../utils/naoPassaram';
import { falhasDaMacro } from '../utils/vocabularioMacro';
import {
  circuloDoPainel,
  competenciaDoMovimento,
  funilDoNo,
  painelDoNo,
  rotuloDaCompetencia,
  textoPct,
  tomDoPainel,
  type CategoriaPainel,
  type FunilDoNo,
  type PainelDoNo,
} from '../utils/painelDoNo';
import { empresaBateRegime, regimeEfetivoNaCadeia } from '../utils/regimeEfetivo';
import {
  areaCoerente,
  bolhaDoNo,
  bolhaIndisponivel,
  indexarAreas,
  indexarBots,
  indexarEtapas,
  indexarGrupos,
  indexarMicroPorArea,
  microNoContexto,
  ressalvasDaArea,
  resumoDoFunil,
  rotuloDaArea,
  textoDaBolha,
  tituloDaBolha,
  tituloDoNo,
} from '../utils/derivarCobertura';
import {
  montarNo,
  NoBoard,
  NoDados,
  VAO_ENTRE_CARTOES,
  VIDRO,
  type ParcelaDoNo,
} from '../components/NoBoard';
import { SLOT_DO_TOUR, TourGuiado, type PassoDoTour } from '../components/TourGuiado';
import { useApelidos } from '../useApelidos';
import { SelectFiltro } from '../components/SelectFiltro';
import { BuscaEmpresa } from '../components/BuscaEmpresa';
import { ordenarFluxos } from '../utils/ordemDoFluxo';
import { montarPlanilhaDoFluxo, type EtapaDoFluxo } from '../utils/exportarFluxo';
import { opcoesDeEmpresas } from '../utils/buscaEmpresa';
import { FiltroStatus } from '../components/FiltroStatus';
import type { AlvoAuditoria } from '../utils/erroDoPopup';
import { PopupProcessos, PopupDados, type AbaDoPainel } from '../components/PopupProcessos';
import { FilaCard, FilaDados, FilaFaixa } from '../components/FilaCard';
import { GrupoLateral, ListaLateral } from '../components/ListaLateral';

/**
 * Aba PROCESSOS — porta o template `isProcessos` do protótipo (`sistema/index.html`
 * linhas 73-379) 1:1: card único com crumbs + filtros Regime/Empresa/Status, título central,
 * board em linhas de dependência (macro de grupos → board de bots → nano de etapas),
 * grid com a lista lateral "Etapas do bot" (só no nano) e o card "Fila", e o popup.
 *
 * Fontes reais por nível (contrato 03/09, mesmas do client `api.ts`):
 * - MACRO: `/esteira` (linhas por cadeia de grupos, dedup no servidor);
 * - BOARD de grupo real: `/grupos/{id}/board`; sentinelas ("Todos os bots"/"Bots sem
 *   grupo"): bots de `/dependencias/bots` + contagens derivadas do `/resumo`;
 * - NANO: `/execucoes/{exec_id}/nano` (trilha REAL de módulos — a heurística
 *   `stepStates()` do protótipo era mock e não migra; sem execução ativa, a tela diz
 *   isso em vez de inventar posição).
 * As listas de empresa (popups e Fila) vêm todas do `/resumo` — fonte única, para nó
 * e popup nunca divergirem (bug histórico do protótipo, corrigido 03/09).
 */

type BotSelecionado = { bot: string; execId: string | null };
type NivelProcessos = 'macro' | 'board' | 'nano';

interface ProcessosTabProps {
  filtros: FiltrosCompartilhados;
  // eslint-disable-next-line no-unused-vars
  onFiltrosChange: (f: FiltrosCompartilhados) => void;
  /** Recorte já filtrado (período + todos os filtros globais) — fonte das listas. */
  empresasFiltradas: IEmpresaResumo[];
  /** Base sem regime/empresa — opções dos selects. */
  empresas: IEmpresaResumo[];
  filtroAtivo: boolean;
  /** Navegação cruzada vinda do Resumo (linha → macro; pílula → nano do bot). */
  comando: ComandoProcessos | null;
  onComandoConsumido: () => void;
  /**
   * Clique na mensagem de erro/aviso do popup → aba Erros já filtrada (09/09).
   * A aba não conhece a query string: só repassa o alvo para o dono da página.
   */
  // eslint-disable-next-line no-unused-vars
  onVerNaAuditoria?: (alvo: AlvoAuditoria) => void;
}

/** `fmtS()` do protótipo — "0,8 s" em pt-BR. */
const fmtS = (s: number): string =>
  `${s.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;

const plural = (n: number, singular: string, plurais: string): string =>
  `${n === 1 ? `1 ${singular}` : `${n} ${plurais}`}`;

/**
 * O CÍRCULO do nó responde "quanto falta", não "quantos erros" (24/09).
 *
 * Até aqui ele mostrava `errs + running` e pintava de vermelho quando havia
 * erro. Isso respondia duas perguntas ao mesmo tempo — "tem problema?" e "tem
 * trabalho pendente?" — e a segunda é a que decide se o bot já pode ser dado
 * como encerrado no dia.
 *
 * Agora: **verde com ✓** quando não resta ninguém para processar, **cinza com o
 * número** quando resta. O erro continua visível na barra e no rodapé (`✓ N ·
 * ✕ N`), onde ele sempre esteve.
 *
 * RESTANTES = `wait + run`, e NÃO inclui `err`: quem falhou JÁ foi processada
 * (com erro), então não está na fila. É o que o pedido diz — "se todas as
 * empresas previstas foram processadas, com erro ou sem, aparece verde".
 *
 * `—` (traço) quando não há dado nenhum: distingue "não sei" de "zero na fila".
 * Inventar ✓ aqui afirmaria que terminou um bot que talvez nem tenha começado.
 */
export const circuloDaFila = (
  restantes: number,
  total: number,
): { circleText: string; tone: 'err' | 'run' | 'ok' | 'idle' } => {
  if (!total) return { circleText: '—', tone: 'idle' };
  if (restantes > 0) return { circleText: String(restantes), tone: 'idle' };
  return { circleText: '✓', tone: 'ok' };
};

/**
 * A cobertura publicada deste grupo, ou `undefined`.
 *
 * `g.id` null é o grupo virtual "Bots sem grupo": ele não existe no cadastro,
 * e desde 25/09 o backend publica a cobertura dele numa área com
 * `departamento_id: null` — por isso o `find` por `grupo_id === null` em vez
 * do índice por id.
 */
const coberturaDoGrupo = (g: IGrupoEsteira, area?: IAreaCobertura) => {
  if (!area) return undefined;
  if (g.id) return indexarGrupos(area).get(g.id);
  return (area.grupos || []).find(x => x.grupo_id === null);
};

/** Tag do nó — mesmos textos do protótipo (linhas 2433-2434, 2584-2585). */
const tagDoNo = (errs: number, paradas: number): string => {
  if (errs) return errs === 1 ? '1 empresa travada' : `${errs} empresas travadas`;
  if (paradas) return paradas === 1 ? '1 empresa aqui' : `${paradas} empresas aqui`;
  return 'sem empresas paradas';
};

const contagensDoBot = (resumo: IEmpresaResumo[], bot: string): IContagens =>
  contagensDaLista(empresasDoBotComStatus(resumo, bot));

const nomeDeEmpresaInfo = (info: IEmpresaInfo | undefined, codiEmp: string): string =>
  info?.nome || info?.razao_social || codiEmp;

/** Converte listas de codi_emp (da fila da execução) em itens exibíveis. */
const listaDeCodis = (
  codis: string[],
  st: StatusEsteira,
  infos: Record<string, IEmpresaInfo> | undefined,
): IEmpresaComStatus[] =>
  codis.map(c => ({ codiEmp: c, nome: nomeDeEmpresaInfo(infos?.[c], c), st, motivo: null }));

/** Texto de estado vazio dentro do card — sem equivalente no protótipo (mock nunca
 *  vazio); mantém o elemento com estado vazio honesto, nunca dado inventado. */
const EstadoVazio: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      padding: '26px 0',
      textAlign: 'center',
      fontSize: '12.5px',
      color: 'oklch(0.55 0.015 165)',
      textWrap: 'pretty',
    }}
  >
    {children}
  </div>
);

export const ProcessosTab: React.FC<ProcessosTabProps> = ({
  filtros,
  onFiltrosChange,
  empresasFiltradas,
  empresas,
  filtroAtivo,
  comando,
  onComandoConsumido,
  onVerNaAuditoria,
}) => {
  const [grupoSel, setGrupoSel] = useState<GrupoSelecionado | null>(null);
  const [botSel, setBotSel] = useState<BotSelecionado | null>(null);
  const [popup, setPopup] = useState<PopupDados | null>(null);
  /** Fluxo (departamento) aberto em FOCO — modal com só as etapas dele. */
  const [fluxoFocado, setFluxoFocado] = useState<string | null>(null);
  const [tourAberto, setTourAberto] = useState(false);
  // O encaixe do "?" fica no cabeçalho da PÁGINA, que monta junto com esta aba —
  // por isso é procurado depois do primeiro render, não durante.
  const [slotDoTour, setSlotDoTour] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlotDoTour(document.getElementById(SLOT_DO_TOUR));
  }, []);

  // Apelido é SÓ exibição: seleção (botSel), queryKeys e filtros seguem o nome real.
  const { apelidoDoBot, temApelido } = useApelidos();

  let nivel: NivelProcessos = 'macro';
  if (botSel) nivel = 'nano';
  else if (grupoSel !== null) nivel = 'board';

  /**
   * O objeto entregue ao popup tem que ser ESTÁVEL entre renders (14/10).
   *
   * Era `{ ...popup, macro }` montado no JSX: um objeto novo a cada render. O
   * popup trata `dados` novo como "fui reaberto" e zera aba, parcela e filtros —
   * e esta aba re-renderiza sozinha (execuções ativas a cada 30 s, histórico e
   * print terminando de carregar). Achado do dono: clicar em "ver print" ou "ver
   * histórico", ou só esperar, jogava o modal de volta para a parcela inicial.
   */
  const dadosDoPopup = useMemo(
    () => (popup ? { ...popup, macro: nivel === 'macro' } : null),
    [popup, nivel],
  );

  const params = useMemo(() => paraParams(filtros.periodo), [filtros.periodo]);
  const chavePeriodo = `${filtros.periodo.de}|${filtros.periodo.ate}`;

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: esteira, isLoading: esteiraLoading } = useQuery({
    queryKey: ['orqEsteira', chavePeriodo],
    queryFn: () => getEsteira(params),
    staleTime: STALE_TEMPO_REAL,
  });

  const { isLoading: resumoLoading } = useQuery({
    queryKey: ['orqResumoProcessos', chavePeriodo],
    queryFn: () => getResumo(params),
    staleTime: STALE_TEMPO_REAL,
  });

  /**
   * COBERTURA por nó e FUNIL por área (17/09).
   *
   * Esta fonte NÃO deriva de `empresasFiltradas`: o
   * denominador é o CADASTRO (espelho de vigência recortado pelo regime da área),
   * e aplicar o recorte do log aqui traria de volta o viés otimista que esta
   * família de endpoints existe para corrigir — a empresa que nunca rodou sumiria
   * do denominador e o percentual subiria sozinho.
   *
   * O período é repassado porque recorta os EVENTOS (quem passou por onde), nunca
   * o universo.
   */
  const { data: cobertura } = useQuery({
    queryKey: ['orqCobertura', chavePeriodo],
    queryFn: () => getCobertura(params),
    staleTime: STALE_TEMPO_REAL,
  });

  const areasPorNome = useMemo(() => indexarAreas(cobertura), [cobertura]);

  /**
   * MOVIMENTO (13/10): quem FATUROU (nota de saída ou serviço) na competência
   * ANTERIOR ao período da tela — em outubro os bots fecham setembro. É o que
   * separa a empresa que não apareceu no fluxo por não ter o que processar da
   * que não apareceu e deveria. Muda no ritmo da carga do ETL, não de bot.
   */
  const competenciaMov = useMemo(
    () => competenciaDoMovimento(filtros.periodo.de),
    [filtros.periodo.de],
  );
  const { data: movimento } = useQuery({
    queryKey: ['orqMovimento', competenciaMov],
    queryFn: () => getMovimento(competenciaMov as string),
    enabled: Boolean(competenciaMov),
    staleTime: 5 * 60 * 1000,
  });
  const comMovimento = useMemo(
    () => (movimento ? new Set(movimento.com_movimento) : null),
    [movimento],
  );

  /**
   * bot -> micro, achatado sobre TODAS as áreas.
   *
   * O board de bots pode estar dentro de um grupo real ou numa sentinela ("Todos
   * os bots"), e nesta última não há área no contexto. Um índice global resolve
   * os dois casos com a mesma leitura.
   *
   * Bot em DUAS áreas (multi-grupo com departamentos diferentes) tem duas
   * coberturas, sobre denominadores diferentes — e não há resposta única. Nesse
   * caso a bolha é OMITIDA em vez de mostrar uma das duas arbitrariamente: metade
   * dos usuários leria o número errado, e o percentual da área continua correto
   * no título da linha. `ambiguo` marca esse caso.
   */
  const microPorBot = useMemo(() => {
    const mapa = new Map<string, { micro: IMicroCobertura; ambiguo: boolean }>();
    (cobertura?.areas ?? []).forEach(a => {
      a.micro.forEach(m => {
        const existente = mapa.get(m.bot);
        mapa.set(m.bot, {
          micro: existente?.micro ?? m,
          ambiguo: existente !== undefined,
        });
      });
    });
    return mapa;
  }, [cobertura]);

  /** (bot, área)→micro: desempata o bot multi-área. Ver `indexarMicroPorArea`. */
  const microPorBotEArea = useMemo(() => indexarMicroPorArea(cobertura), [cobertura]);

  /**
   * Área (departamento) do grupo aberto, lida da ESTEIRA (`grupo.departamento_id`)
   * — não de `gruposConfig`/`boardGrupo`, que só são declarados abaixo e ainda não
   * existem neste ponto do componente. Sentinela ("Todos os bots"/"Sem grupo") e
   * grupo sem departamento devolvem null, e a bolha cai no caminho antigo.
   */
  const areaDoGrupoAberto = useMemo((): string | null => {
    if (grupoSel === null || !ehGrupoReal(grupoSel)) return null;
    const grupo = (esteira?.linhas ?? []).flatMap(l => l.grupos).find(g => g.id === grupoSel);
    return grupo?.departamento_id ?? null;
  }, [grupoSel, esteira]);

  const microDoBotSelecionado = useCallback(
    (bot: string): IMicroCobertura | null =>
      microNoContexto(bot, areaDoGrupoAberto, microPorBotEArea, microPorBot),
    [microPorBot, microPorBotEArea, areaDoGrupoAberto],
  );

  /**
   * MICRO — quem concluiu no BOT ANTERIOR e não chegou a este (07/10).
   *
   * Só com dependência cadastrada (`dep_bot`), por pedido explícito do dono:
   * *"comparando o bot com bot somente se eles estiverem interligados com
   * dependência"*. Sem ela não existe "etapa anterior" e qualquer número seria
   * inventado — o indicador some.
   */
  const naoChegaramNesteBot = useCallback(
    (bot: string, depBot: string | null): NaoChegaram | null => {
      if (grupoSel === null || !ehGrupoReal(grupoSel)) return null;
      const linhaGrupo = (esteira?.linhas ?? [])
        .flatMap(l => l.grupos)
        .find(g => g.id === grupoSel);
      if (!linhaGrupo?.departamento) return null;
      return naoChegaramAoBot(bot, depBot, areasPorNome.get(linhaGrupo.departamento));
    },
    [grupoSel, esteira, areasPorNome],
  );

  /**
   * POR QUE este bot não tem percentual — a frase exata, não um genérico.
   *
   * São três causas distintas e o usuário age diferente em cada uma: a ambígua é
   * cadastro (o bot está em dois departamentos), a ausente é o bot não estar
   * mapeado em área nenhuma, e o carregando é transitório. Dizer "sem percentual"
   * para as três esconde justamente o que dá para consertar.
   */
  const motivoSemMicro = useCallback(
    (bot: string): string => {
      if (!cobertura) return 'cobertura ainda carregando';
      if (microPorBot.get(bot)?.ambiguo) {
        return 'este bot pertence a duas áreas com denominadores diferentes — não há percentual único';
      }
      return 'este bot não está vinculado a uma área com denominador conhecido';
    },
    [cobertura, microPorBot],
  );

  /** Etapas do funil do bot ABERTO no nano — vazio fora do nano. */
  const etapasDoBotSelecionado = useMemo(
    () => indexarEtapas(botSel ? (microDoBotSelecionado(botSel.bot) ?? undefined) : undefined),
    [botSel, microDoBotSelecionado],
  );

  const grupoRealSel = grupoSel !== null && ehGrupoReal(grupoSel) ? grupoSel : null;

  const { data: boardGrupo, isLoading: boardLoading } = useQuery({
    queryKey: ['orqGrupoBoard', grupoSel, chavePeriodo],
    queryFn: () => getGrupoBoard(grupoRealSel as string, params),
    enabled: grupoRealSel !== null,
    staleTime: STALE_TEMPO_REAL,
  });

  const ehSentinela = grupoSel === SEM_GRUPO_ID || grupoSel === TODOS_GRUPOS_ID;
  // Sempre habilitada (regime por nível, 08/09): além do board das sentinelas, os
  // vínculos carregam o `regime` de cada bot, usado no filtro de TODOS os níveis.
  // Mesma queryKey da página (index.tsx) — um cache só, sem fetch extra.
  const { data: vinculos, isLoading: vinculosLoading } = useQuery({
    queryKey: ['orqVinculosBots'],
    queryFn: () => listarVinculosBots(),
  });
  // Config de grupos/departamentos (mesmas queryKeys da aba Dependências): fonte
  // dos regimes por nível da cadeia grupo → departamento.
  const { data: gruposConfig } = useQuery({
    queryKey: ['orqDepGrupos'],
    queryFn: () => listarGrupos(),
  });
  const { data: departamentosConfig } = useQuery({
    queryKey: ['orqDepDepartamentos'],
    queryFn: () => listarDepartamentos(),
  });

  // Mapa bot → exec ativa: dá o exec_id do "Ver etapas" e mantém o nano achável.
  const { data: ativas } = useQuery({
    queryKey: ['orqExecucoesAtivasV2'],
    queryFn: () => getExecucoesAtivas(),
    refetchInterval: 30000,
    retry: 1,
  });

  const execDoBot = useMemo(() => {
    const mapa = new Map<string, string>();
    (ativas ?? []).forEach(e => mapa.set(e.bot, e.exec_id));
    return mapa;
  }, [ativas]);

  // Resolvido no RENDER (não congelado no clique): se a lista de ativas chegar depois
  // da navegação (ex.: pílula do Resumo), o nano encontra a execução assim mesmo.
  const execId = botSel ? (botSel.execId ?? execDoBot.get(botSel.bot) ?? null) : null;

  // POLLING DE 5s SÓ NO NANO (pedido 08/09: "ver as empresas andando pelos módulos
  // quase em tempo real" — o selo "Ao vivo" passa a ser verdadeiro). As 3 queries
  // abaixo são as ÚNICAS que alimentam a visão nano (trilha de módulos + cabeçalho
  // "N de M" + fila da execução) e já são gated por `enabled: Boolean(execId)` —
  // execId só existe com bot selecionado (nível nano); ao sair do nano (voltar ao
  // board/macro, trocar de aba desmonta o componente) o enabled cai e o TanStack
  // PARA o refetchInterval: zero polling residual. Custo por tick: 3 GETs escopados
  // à execução (nano + detalhe + fila/pendentes) — o /resumo e o /esteira (caros,
  // cache de 300s) NÃO são refeitos.
  const { data: nano, isLoading: nanoLoading } = useQuery({
    queryKey: ['orqNano', execId, filtros.empresa],
    queryFn: () => getExecucaoNano(execId as string, filtros.empresa ?? undefined),
    enabled: Boolean(execId),
    refetchInterval: 5000,
  });
  const { data: execDetalhe } = useQuery({
    queryKey: ['orqExecDetalhe', execId],
    queryFn: () => getExecucaoDetalhe(execId as string),
    enabled: Boolean(execId),
    refetchInterval: 5000,
  });
  const { data: filaExec } = useQuery({
    queryKey: ['orqFilaPendentes', execId],
    queryFn: () => getExecucaoFilaPendentes(execId as string),
    enabled: Boolean(execId),
    refetchInterval: 5000,
  });

  // ── Filtros compartilhados (regime/empresa, mão única) ─────────────────────
  const regimes = useMemo(() => regimesDisponiveis(empresas), [empresas]);
  const opcoesEmpresa = useMemo(
    () => empresasComLog(empresas, filtros.regime),
    [empresas, filtros.regime],
  );
  /**
   * As mesmas opções de empresa, no formato pesquisável da `BuscaEmpresa`.
   *
   * Deriva de `opcoesEmpresa` (não de `empresas`) de propósito: o recorte por
   * regime continua valendo para a busca, exatamente como valia para o select.
   * `nomeEmpresa` é injetado para a resolução do nome continuar vindo de um lugar
   * só — reimplementá-la dentro do utilitário criaria a segunda fonte de verdade
   * que este repositório já paga caro em outros pontos.
   */
  /** Modo da busca de empresa — ID primeiro, como nas outras abas (19/10). */
  const [modoEmpresa, setModoEmpresa] = useState<'id' | 'razao'>('id');
  const [codigoDigitado, setCodigoDigitado] = useState(filtros.empresa ?? '');
  // Empresa escolhida por fora (clique no Resumo, "limpar filtros") reflete no campo.
  useEffect(() => {
    setCodigoDigitado(atual =>
      atual.trim() === (filtros.empresa ?? '') ? atual : (filtros.empresa ?? ''),
    );
  }, [filtros.empresa]);
  const opcoesBuscaEmpresa = useMemo(
    () => opcoesDeEmpresas(opcoesEmpresa, nomeEmpresa),
    [opcoesEmpresa],
  );
  const empresaFiltradaNome = useMemo(() => {
    if (!filtros.empresa) return null;
    const achada = empresas.find(e => e.codi_emp === filtros.empresa);
    return achada ? nomeEmpresa(achada) : filtros.empresa;
  }, [filtros.empresa, empresas]);

  /** Mudar regime/empresa zera nano e popup — a seleção pode nem existir no recorte. */
  const aplicarFiltros = (novos: FiltrosCompartilhados) => {
    setBotSel(null);
    setPopup(null);
    onFiltrosChange(novos);
  };

  // ── Filtro de STATUS (pedido 04/09) — recorte pelo status NAQUELE contexto ─
  // As listas dos popups/card Fila já carregam o status de cada empresa no bot ou
  // grupo; o filtro corta AQUI, antes de qualquer agregação (contagens/barras/meta
  // derivam da lista filtrada) — regra de ouro do protótipo: nó e popup nunca
  // divergem. Alternar status preserva o nível navegado (o bot continua existindo);
  // só o popup fecha, pois seu conteúdo é congelado no clique.
  const alternarStatus = (st: StatusEsteira) => {
    setPopup(null);
    onFiltrosChange(aoAlternarStatus(filtros, st));
  };
  const limparStatus = () => {
    setPopup(null);
    onFiltrosChange({ ...filtros, status: [] });
  };
  /** Nenhum selecionado = tudo permitido (usado nos contadores do nível nano). */
  const stOk = useCallback(
    (st: StatusEsteira) => statusPermitido(filtros.status, st),
    [filtros.status],
  );
  // ── REGIME POR NÍVEL (pedido 08/09) — filtro NA FONTE, por contexto ────────
  // Regime efetivo com herança departamento → grupo → bot (o mais específico
  // vence), CONTEXTUAL por cadeia: dentro de grupo real vale a cadeia daquele
  // grupo; nas sentinelas ("Todos os bots"/"Bots sem grupo") e no grupo virtual
  // do macro vale só o regime PRÓPRIO do bot. O recorte entra na MESMA derivação
  // que alimenta nó do board, card Fila e popups — nunca divergem.
  const regimeDoBot = useMemo(
    () => new Map<string, string | null>((vinculos ?? []).map(v => [v.bot, v.regime ?? null])),
    [vinculos],
  );
  const grupoConfigPorId = useMemo(
    () => new Map((gruposConfig ?? []).map(g => [g.id, g])),
    [gruposConfig],
  );
  const departamentoConfigPorId = useMemo(
    () => new Map((departamentosConfig ?? []).map(d => [d.id, d])),
    [departamentosConfig],
  );
  /** Regime efetivo do bot no contexto: cadeia do grupo real, ou próprio (id null). */
  const regimeNoContexto = useCallback(
    (bot: string, grupoId: string | null): string | null => {
      const grupo = grupoId !== null ? grupoConfigPorId.get(grupoId) : undefined;
      const departamento = grupo?.departamento_id
        ? departamentoConfigPorId.get(grupo.departamento_id)
        : undefined;
      return regimeEfetivoNaCadeia({ regime: regimeDoBot.get(bot) ?? null }, grupo, departamento);
    },
    [regimeDoBot, grupoConfigPorId, departamentoConfigPorId],
  );
  /** Base do /resumo recortada pelo regime efetivo do bot NAQUELE contexto. */
  const baseDoBotNoContexto = useCallback(
    (grupoId: string | null) =>
      (bot: string): IEmpresaResumo[] => {
        const regime = regimeNoContexto(bot, grupoId);
        if (regime === null) return empresasFiltradas;
        return empresasFiltradas.filter(e => empresaBateRegime(e.empresa, regime));
      },
    [empresasFiltradas, regimeNoContexto],
  );
  // Existe regime configurado em ALGUM nível — hoje serve só ao chip "Recorte por
  // regime ativo" (M3). NÃO é mais gate de rederivação: as contagens do nó saem
  // SEMPRE da mesma lista filtrada do popup/Fila (ver `linhasMacro`).
  const regimePorNivelAtivo = useMemo(
    () =>
      (vinculos ?? []).some(v => v.regime) ||
      (gruposConfig ?? []).some(g => g.regime) ||
      (departamentosConfig ?? []).some(d => d.regime),
    [vinculos, gruposConfig, departamentosConfig],
  );
  // Níveis com regime configurado — alimenta o title do chip "Recorte por regime
  // ativo" (correção M3 da revisão 08/09: o recorte era invisível na aba).
  const niveisComRegime = useMemo(() => {
    const partes: string[] = [];
    (vinculos ?? []).forEach(v => {
      if (v.regime) partes.push(`Bot ${apelidoDoBot(v.bot)}: ${v.regime}`);
    });
    (gruposConfig ?? []).forEach(g => {
      if (g.regime) partes.push(`Grupo ${g.nome}: ${g.regime}`);
    });
    (departamentosConfig ?? []).forEach(d => {
      if (d.regime) partes.push(`Departamento ${d.nome}: ${d.regime}`);
    });
    return partes;
  }, [vinculos, gruposConfig, departamentosConfig, apelidoDoBot]);

  /** Empresas do BOT no recorte global + regime do contexto + status naquele bot. */
  const listaDoBotFiltrada = useCallback(
    (bot: string) =>
      filtrarPorStatus(
        empresasDoBotComStatus(baseDoBotNoContexto(grupoRealSel)(bot), bot),
        filtros.status,
      ),
    [baseDoBotNoContexto, grupoRealSel, filtros.status],
  );
  /** Empresas do GRUPO (dedup por empresa), com regime da cadeia POR BOT + status. */
  const listaDoGrupoFiltrada = useCallback(
    (grupo: { id: string | null; bots: string[] }) =>
      filtrarPorStatus(
        empresasDoGrupoComStatus(empresasFiltradas, grupo.bots, baseDoBotNoContexto(grupo.id)),
        filtros.status,
      ),
    [empresasFiltradas, baseDoBotNoContexto, filtros.status],
  );

  // ── PAINEL DO NÓ (13/10) — ver `utils/painelDoNo` ──────────────────────────
  // Universo (vigentes do regime) repartido em ok · erro · em andamento · não
  // constam · sem movimento, por etapa E por RPA, com o funil entre etapas.
  const nomesDasEmpresas = useMemo(() => {
    const mapa = new Map<string, string>();
    empresas.forEach(e => mapa.set(e.codi_emp, nomeEmpresa(e)));
    return mapa;
  }, [empresas]);
  /** Com filtro global (regime/empresa), o universo encolhe JUNTO com as listas. */
  const codisNoRecorte = useMemo(
    () => (filtroAtivo ? new Set(empresasFiltradas.map(e => e.codi_emp)) : null),
    [filtroAtivo, empresasFiltradas],
  );
  /**
   * O universo nomeado da área, ou `null` quando o painel não pode ser montado:
   * sem a lista do `/cobertura` não há como dizer QUEM não consta, e com filtro
   * de STATUS ativo as listas já chegam recortadas (o painel diria "não consta"
   * de quem só foi filtrada) — nos dois casos o nó cai no desenho antigo.
   */
  const universoDaArea = useCallback(
    (area?: IAreaCobertura): string[] | null => {
      if (filtros.status.length) return null;
      const universo = area?.universo_empresas;
      if (!universo || !universo.length) return null;
      return codisNoRecorte ? universo.filter(c => codisNoRecorte.has(c)) : universo;
    },
    [filtros.status, codisNoRecorte],
  );
  const painelDoGrupo = useCallback(
    (g: { id: string | null; bots: string[] }, area?: IAreaCobertura) => {
      const universo = universoDaArea(area);
      if (!universo) return null;
      const base = baseDoBotNoContexto(g.id);
      const lista = empresasDoGrupoComStatus(empresasFiltradas, g.bots, base);
      const porBot = g.bots.map(bot => {
        const listaBot = empresasDoBotComStatus(base(bot), bot);
        return { bot, lista: listaBot, painel: painelDoNo(universo, listaBot, comMovimento) };
      });
      return { painel: painelDoNo(universo, lista, comMovimento), lista, porBot };
    },
    [universoDaArea, baseDoBotNoContexto, empresasFiltradas, comMovimento],
  );
  /** Área do grupo aberto no board, já conferida — fonte do universo dos RPAs. */
  const areaDoBoard = useMemo((): IAreaCobertura | undefined => {
    if (grupoRealSel === null) return undefined;
    const grupo = (esteira?.linhas ?? []).flatMap(l => l.grupos).find(g => g.id === grupoRealSel);
    const area = grupo?.departamento ? areasPorNome.get(grupo.departamento) : undefined;
    return area && areaCoerente(area) ? area : undefined;
  }, [grupoRealSel, esteira, areasPorNome]);
  const painelDoBot = useCallback(
    (bot: string) => {
      const universo = universoDaArea(areaDoBoard);
      if (!universo) return null;
      const lista = empresasDoBotComStatus(baseDoBotNoContexto(grupoRealSel)(bot), bot);
      return { painel: painelDoNo(universo, lista, comMovimento), lista };
    },
    [universoDaArea, areaDoBoard, baseDoBotNoContexto, grupoRealSel, comMovimento],
  );
  const rotuloMovimento = rotuloDaCompetencia(movimento?.competencia ?? competenciaMov);

  // ── EXCEL DO FLUXO (19/10) — ver `utils/exportarFluxo` ─────────────────────
  // "tirar um Excel de cada fluxo... empresa, status, erro para passar pro
  // time". As linhas saem das MESMAS listas dos cartões; o backend só formata.
  const [exportando, setExportando] = useState<string | null>(null);
  const [segundosExportando, setSegundosExportando] = useState(0);
  const [avisoExportacao, setAvisoExportacao] = useState<{ ok: boolean; texto: string } | null>(
    null,
  );
  useEffect(() => {
    if (exportando === null) {
      setSegundosExportando(0);
      return undefined;
    }
    const inicio = Date.now();
    const relogio = setInterval(
      () => setSegundosExportando(Math.floor((Date.now() - inicio) / 1000)),
      1000,
    );
    // Fechar ou recarregar a aba no meio perde o arquivo: o navegador pergunta antes.
    const avisarAoSair = (ev: BeforeUnloadEvent) => {
      ev.preventDefault();
      // eslint-disable-next-line no-param-reassign
      ev.returnValue = '';
    };
    window.addEventListener('beforeunload', avisarAoSair);
    return () => {
      clearInterval(relogio);
      window.removeEventListener('beforeunload', avisarAoSair);
    };
  }, [exportando]);
  useEffect(() => {
    if (!avisoExportacao) return undefined;
    const t = setTimeout(() => setAvisoExportacao(null), 8000);
    return () => clearTimeout(t);
  }, [avisoExportacao]);

  const exportarFluxo = async (fluxo: string, grupos: IGrupoEsteira[]) => {
    if (exportando !== null) return;
    setExportando(fluxo);
    setAvisoExportacao(null);
    try {
      const area = areasPorNome.get(fluxo);
      const areaOk = area && areaCoerente(area) ? area : undefined;
      const etapasBase = grupos.map(g => {
        const base = baseDoBotNoContexto(g.id);
        return {
          g,
          base,
          itens: Boolean(coberturaDoGrupo(g, areaOk)?.cobertura_itens),
          listas: g.bots.map(bot => ({ bot, lista: empresasDoBotComStatus(base(bot), bot) })),
        };
      });
      // O universo do cartão (vigentes do regime da área, no recorte da tela).
      // Sem cobertura, cai na união de quem tem registro — e o arquivo avisa.
      const universoDaCobertura = areaOk?.universo_empresas?.length
        ? areaOk.universo_empresas.filter(c => !codisNoRecorte || codisNoRecorte.has(c))
        : null;
      const universo =
        universoDaCobertura ??
        Array.from(
          new Set(
            etapasBase
              .filter(e => !e.itens)
              .flatMap(e => e.listas.flatMap(l => l.lista.map(i => i.codiEmp))),
          ),
        );
      const etapas: EtapaDoFluxo[] = etapasBase.map(e => ({
        nome: e.g.nome,
        itens: e.itens,
        painel: painelDoNo(
          universo,
          empresasDoGrupoComStatus(empresasFiltradas, e.g.bots, e.base),
          comMovimento,
        ),
        bots: e.listas.map(l => ({ ...l, painel: painelDoNo(universo, l.lista, comMovimento) })),
      }));
      const dia = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');
      const filtrosTexto = [
        filtros.regime && `regime ${filtros.regime}`,
        filtros.empresa && `empresa ${filtros.empresa}`,
        filtros.status.length && `status ${filtros.status.join(', ')}`,
      ].filter(Boolean);
      const agora = new Date();
      const contexto = [
        `Período: ${dia(filtros.periodo.de)} a ${dia(filtros.periodo.ate)}`,
        `Empresas do fluxo: ${universo.length}${
          universoDaCobertura ? '' : ' (sem total da cobertura: só as que têm registro)'
        }`,
        comMovimento
          ? `Movimento: notas de saída ou serviço em ${rotuloMovimento}`
          : 'Movimento: ainda não carregado (ninguém aparece como "sem movimento")',
        filtrosTexto.length ? `Filtros da tela: ${filtrosTexto.join(' · ')}` : 'Sem filtros',
        `Gerado em ${agora.toLocaleString('pt-BR')}`,
      ];
      const corpo = montarPlanilhaDoFluxo({
        fluxo,
        etapas,
        universo,
        comMovimento,
        empresas: new Map(empresas.map(e => [e.codi_emp, e])),
        apelidoDoBot,
        contexto,
        periodo: `${filtros.periodo.de.slice(0, 10)} a ${filtros.periodo.ate.slice(0, 10)}`,
      });
      const blob = await baixarExcelDoFluxo(corpo);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fluxo.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'fluxo'} - ${dia(
        filtros.periodo.de,
      ).replace(/\//g, '-')} a ${dia(filtros.periodo.ate).replace(/\//g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      const nErros = corpo.abas.find(a => a.nome === 'Erros')?.linhas.length ?? 0;
      setAvisoExportacao({
        ok: true,
        texto: `Planilha de ${fluxo} baixada: ${universo.length} empresas, ${nErros} erro(s) ou alerta(s) na aba Erros.`,
      });
    } catch {
      setAvisoExportacao({
        ok: false,
        texto: `Não deu para gerar a planilha de ${fluxo}. Tente de novo em instantes.`,
      });
    } finally {
      setExportando(null);
    }
  };

  const botaoExcel = (fluxo: string, grupos: IGrupoEsteira[]) => (
    <button
      type="button"
      onClick={ev => {
        ev.stopPropagation();
        exportarFluxo(fluxo, grupos);
      }}
      disabled={exportando !== null}
      title="Baixa um Excel deste fluxo: resumo das etapas, erros por empresa e RPA, situação de cada empresa e o detalhe completo, no recorte atual da tela"
      aria-label={`Exportar ${fluxo} em Excel`}
      style={{
        ...VIDRO,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '28px',
        padding: '0 11px',
        borderRadius: '10px',
        font: 'inherit',
        fontSize: '12px',
        fontWeight: 600,
        color: 'oklch(0.4 0.11 150)',
        cursor: exportando !== null ? 'wait' : 'pointer',
        opacity: exportando !== null && exportando !== fluxo ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {exportando === fluxo ? (
        <span className="orq-girar" aria-hidden="true" />
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
          <path
            d="M8 8l8 8M16 8l-8 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
      {exportando === fluxo ? 'Gerando…' : 'Excel'}
    </button>
  );

  /** As linhas do cartão: uma por parcela, com número, percentual e clique. */
  const parcelasDoCartao = (
    p: PainelDoNo,
    funil: FunilDoNo | null,
    // eslint-disable-next-line no-unused-vars
    abrir: (categoria?: CategoriaPainel | 'naoSeguiram', movimento?: 'com' | 'sem') => void,
    erroParcial?: number,
  ): ParcelaDoNo[] => {
    const cinza = 'oklch(0.5 0.015 165)';
    const linhas: ParcelaDoNo[] = [
      {
        chave: 'ok',
        icone: '✓',
        rotulo: 'Concluídas',
        valor: String(p.ok.length),
        pct: textoPct(p.ok.length, p.base),
        cor: C.greenFg,
        title: `${p.ok.length} de ${p.base} empresas concluíram`,
        onSelect: () => abrir('ok'),
      },
      {
        chave: 'erro',
        icone: '✕',
        rotulo: 'Erro ou alerta',
        valor: String(p.erro.length),
        pct: textoPct(p.erro.length, p.base),
        cor: p.erro.length ? C.redFg : cinza,
        title: erroParcial
          ? `${p.erro.length} com erro ou alerta — ${erroParcial} delas concluíram em parte dos RPAs desta etapa (erro parcial)`
          : 'Último resultado da empresa aqui foi erro ou alerta. Quem errou e depois passou conta como concluída.',
        onSelect: () => abrir('erro'),
      },
    ];
    if (p.andamento.length) {
      linhas.push({
        chave: 'andamento',
        icone: '⏳',
        rotulo: 'Em andamento',
        valor: String(p.andamento.length),
        pct: textoPct(p.andamento.length, p.base),
        cor: C.blueFg,
        title: 'Rodando agora ou na fila',
        onSelect: () => abrir('andamento'),
      });
    }
    linhas.push({
      chave: 'naoConstam',
      icone: '↳',
      rotulo: 'Não constam no fluxo',
      valor: String(p.naoConstam.length),
      pct: textoPct(p.naoConstam.length, p.base),
      cor: p.naoConstam.length ? C.amberFg : cinza,
      title: `Empresas do regime, com movimento, que ainda não concluíram nem erraram aqui: sem nenhum registro, ou só com log de "pendente" (passou mas não pegou nada)${
        p.pendentes.length ? ` — ${p.pendentes.length} com log de pendente` : ''
      }`,
      onSelect: () => abrir('naoConstam'),
    });
    if (p.movimentoConhecido) {
      // "Sem movimento" sozinho aqui foi lido como "todas as sem movimento" e o
      // número não batia (dono, 13/10): esta parcela é só de quem não faturou E
      // não tem registro na etapa. O grupo inteiro vem nas duas linhas abaixo.
      linhas.push({
        chave: 'semMovimento',
        icone: '∅',
        rotulo: 'Sem movimento e sem registro',
        valor: String(p.semMovimento.length),
        pct: textoPct(p.semMovimento.length, p.base),
        cor: cinza,
        title: `Não têm nota de saída nem de serviço em ${rotuloMovimento} E não têm registro nesta etapa. Contam para a meta de 100% como todas as outras. As sem movimento que o bot processou estão em concluídas/erro.`,
        onSelect: () => abrir('semMovimento'),
      });
    }
    if (p.porMovimento) {
      // OS DOIS GRUPOS INTEIROS (13/10, regra do dono: "se a empresa não tem nota
      // de saída e/ou serviço é que ela é sem movimento"): todas as que faturaram
      // e todas as que não, cada grupo com o SEU ok e erro. É outro corte das
      // mesmas empresas — não soma com as parcelas acima.
      (
        [
          // Cores do dono (14/10): com movimento azul, sem movimento vermelho.
          ['movCom', '●', 'Com movimento', 'Com mov.', C.blueFg, p.porMovimento.com],
          ['movSem', '○', 'Sem movimento', 'Sem mov.', C.redFg, p.porMovimento.sem],
        ] as const
      ).forEach(([chave, icone, nome, curto, corDoLado, lado]) => {
        // AS TRÊS FATIAS do grupo (dono, 13/10: "as empresas que não têm erro e
        // nem conclusão eu preciso saber também, para poder atacar elas"): ok,
        // erro e as que não passaram — sem a terceira o grupo não fechava 100%.
        // UMA LINHA só (14/10): só os percentuais no cartão; os números
        // absolutos ficam no tooltip e no modal.
        const naoPassaram = lado.ausentes + lado.andamento;
        linhas.push({
          chave,
          icone,
          compacto: true,
          rotulo: `${curto} ${lado.total}`,
          valor: String(lado.total),
          detalhe: lado.total
            ? `✓${textoPct(lado.ok, lado.total)} ✕${textoPct(lado.erro, lado.total)} ↳${textoPct(naoPassaram, lado.total)}`
            : '—',
          cor: corDoLado,
          title: `${nome} em ${rotuloMovimento} (nota de saída ou serviço): ${lado.total} das ${p.base} empresas do regime (${textoPct(lado.total, p.base)}). Destas: ✓ ${lado.ok} concluídas (${textoPct(lado.ok, lado.total)}), ✕ ${lado.erro} com erro ou alerta (${textoPct(lado.erro, lado.total)}) e ↳ ${naoPassaram} sem erro e sem conclusão nesta etapa (${textoPct(naoPassaram, lado.total)}). Clique para ver só este grupo.`,
          onSelect: () => abrir(undefined, chave === 'movCom' ? 'com' : 'sem'),
        });
      });
    }
    if (funil) {
      linhas.push({
        chave: 'funil',
        icone: '⇢',
        // "Vieram de X" não se explicava sozinho (dono, 13/10). A frase agora
        // diz as duas pontas: das que concluíram na etapa anterior, quantas já
        // chegaram nesta.
        rotulo: `Das ${funil.okAnterior} concluídas em ${funil.anterior}, chegaram aqui`,
        valor: String(funil.seguiram.length),
        pct: textoPct(funil.seguiram.length, funil.okAnterior),
        cor: funil.naoSeguiram.length ? C.amberFg : C.greenFg,
        title: `Das ${funil.okAnterior} que concluíram em ${funil.anterior}, ${funil.seguiram.length} já têm registro aqui e ${funil.naoSeguiram.length} não seguiram`,
        onSelect: () => abrir('naoSeguiram'),
      });
    }
    return linhas;
  };

  // ── Navegação ──────────────────────────────────────────────────────────────
  /**
   * MUDAR DE NÍVEL LIMPA O FILTRO DE STATUS (24/09).
   *
   * A macro fala em GRUPOS ("Falha de processamento" = err + warn) e o board/nano
   * falam em status soltos. Uma seleção feita num vocabulário não tem tradução
   * honesta no outro: descer com "Falha de processamento" marcado deixaria duas
   * pílulas acesas que o usuário não clicou, e subir com só "Alerta" marcado
   * acenderia "Falha de processamento" inteira — afirmando um filtro mais largo
   * do que o pedido.
   *
   * Limpar é a escolha do dono, e é a única que nunca mente sobre o que está
   * filtrado. O custo (perder a seleção ao navegar) é visível; o de traduzir
   * errado, não.
   */
  const limparStatusAoTrocarNivel = () => {
    if (filtros.status.length) limparStatus();
  };

  const voltarMacro = () => {
    limparStatusAoTrocarNivel();
    setGrupoSel(null);
    setBotSel(null);
    setPopup(null);
  };

  const abrirBoard = (grupo: GrupoSelecionado) => {
    limparStatusAoTrocarNivel();
    setPopup(null);
    setBotSel(null);
    setGrupoSel(grupo);
  };

  const abrirNano = (bot: string, execIdConhecido: string | null) => {
    limparStatusAoTrocarNivel();
    setPopup(null);
    setBotSel({ bot, execId: execIdConhecido ?? execDoBot.get(bot) ?? null });
  };

  // Comando de navegação cruzada (Resumo → Processos), consumido uma única vez.
  useEffect(() => {
    if (!comando) return;
    setPopup(null);
    if (comando.tipo === 'macro') {
      setGrupoSel(null);
      setBotSel(null);
    } else {
      setGrupoSel(TODOS_GRUPOS_ID);
      setBotSel({ bot: comando.bot, execId: null });
    }
    onComandoConsumido();
  }, [comando, onComandoConsumido]);

  const nomeGrupoSel = useMemo(() => {
    if (grupoSel === TODOS_GRUPOS_ID) return 'Todos os bots';
    if (grupoSel === SEM_GRUPO_ID) return 'Bots sem grupo';
    if (grupoSel !== null) {
      const achado = (esteira?.linhas ?? []).flatMap(l => l.grupos).find(g => g.id === grupoSel);
      return boardGrupo?.grupo.nome ?? achado?.nome ?? `Grupo ${grupoSel}`;
    }
    return '';
  }, [grupoSel, esteira, boardGrupo]);

  // ── Crumbs (procCrumbs() do protótipo, linhas 2180-2216) ───────────────────
  const crumbs = useMemo(() => {
    const itens: Array<{ label: string; title?: string; active: boolean; onSelect: () => void }> = [
      { label: 'Todos os grupos', active: nivel === 'macro', onSelect: voltarMacro },
    ];
    if (grupoSel !== null) {
      itens.push({
        label: nomeGrupoSel,
        active: nivel === 'board',
        onSelect: () => {
          setBotSel(null);
          setPopup(null);
        },
      });
    }
    // "Ver todos os bots →": só dentro de um grupo REAL, no nível board.
    if (grupoSel !== null && grupoSel !== TODOS_GRUPOS_ID && !botSel) {
      itens.push({
        label: 'Ver todos os bots →',
        active: false,
        onSelect: () => {
          setPopup(null);
          setGrupoSel(TODOS_GRUPOS_ID);
        },
      });
    }
    if (botSel) {
      itens.push({
        label: apelidoDoBot(botSel.bot),
        title: temApelido(botSel.bot) ? botSel.bot : undefined,
        active: true,
        onSelect: () => {},
      });
    }
    return itens.map((c, i) => ({
      label: c.label,
      title: c.title,
      num: String(i + 1),
      bg: c.active ? C.dark : C.white,
      color: c.active ? C.onDark : C.ink,
      numColor: c.active ? 'oklch(0.78 0.13 162)' : 'oklch(0.55 0.09 165)',
      border: c.active ? C.dark : C.border,
      onSelect: c.onSelect,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, grupoSel, botSel, nomeGrupoSel, apelidoDoBot, temApelido]);

  // ── Popups (clique em nó NUNCA navega direto — sempre popup primeiro) ──────
  const abrirPopupGrupo = (
    grupo: IGrupoEsteira,
    anterior: IGrupoEsteira | undefined,
    naoChegaram?: NaoChegaram | null,
    /**
     * As parcelas do nó clicado, para a seção "não rodaram" do popup medir o
     * `↳` pela MESMA régua da bolha — ver `parcelas` em `PopupDados`.
     */
    parcelas?: ParcelasDoNo | null,
  ) => {
    const lista = listaDoGrupoFiltrada(grupo);
    setPopup({
      title: grupo.nome,
      sub: anterior ? `Depende de ${anterior.nome}` : 'Sem dependência — roda em paralelo',
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      actionLabel: 'Ver bots →',
      onAction: () => abrirBoard(grupo.id ?? SEM_GRUPO_ID),
      // `null` quando não há ausente — a seção some em vez de afirmar "todas
      // chegaram" com uma lista vazia. É a MESMA régua do `NoBoard` (`total >
      // 0`): a decisão vive em quem exibe, não duplicada em quem calcula.
      //
      // O `naoChegou` DO NÓ também segura o objeto (09/10): a régua passou a ser
      // o universo do departamento, e `itens` continua sendo a união dos
      // agrupamentos. Numa área de um nó só — Departamento Pessoal — todo mundo
      // que tem log está nele, `itens` fica VAZIO e `naoChegou` vale 680: o nó
      // exibia "↳ 680 não rodaram" e o popup não exibia seção nenhuma.
      // Zerar aqui matava a seção antes de `PopupProcessos` poder decidir.
      naoChegaram: naoChegaram?.itens.length || parcelas?.naoChegou ? naoChegaram : null,
      parcelas,
    });
  };

  const abrirPopupBot = (
    bot: IBotBoard,
    anterior: IBotBoard | undefined,
    naoChegaram?: NaoChegaram | null,
  ) => {
    const lista = listaDoBotFiltrada(bot.bot);
    setPopup({
      title: apelidoDoBot(bot.bot),
      titleTooltip: temApelido(bot.bot) ? bot.bot : undefined,
      sub: anterior
        ? `Depende de ${apelidoDoBot(anterior.bot)}`
        : 'Sem dependência — roda em paralelo',
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      actionLabel: 'Ver etapas →',
      onAction: () => abrirNano(bot.bot, bot.exec_id),
      naoChegaram: naoChegaram?.itens.length ? naoChegaram : null,
    });
  };

  // ── Macro: linhas do /esteira com as contagens SEMPRE rederivadas do /resumo
  // (nó = popup = Fila, sempre — a regra de ouro de `filtrosCompartilhados.ts`).
  //
  // Até 15/09 a rederivação era condicionada a `filtroAtivo || regimePorNivelAtivo`.
  // Como o PERÍODO não entra em `filtroAtivo` (index.tsx), a tela sem nenhum chip
  // de filtro caía no `return linhas` e o nó passava a exibir a contagem AGREGADA
  // DO BFF — que discorda da lista por construção: `_melhor_status`
  // (done:0, err:1, warn:2) colapsa em `done` a empresa que concluiu num bot e
  // alertou em outro, então `contagens.warn` chegava ZERO e o `⚠` nunca era
  // impresso, enquanto o popup (derivado) mostrava o alerta. Derivar sempre é o
  // único jeito de o nó não mentir; o custo é O(empresas) sobre dado já em memória.
  //
  // O PRUNING (sumir com nó zerado) continua valendo SÓ com filtro global ativo —
  // correção M2 da revisão 08/09: regime configurado num nível NÃO pode fazer
  // grupo ocioso de cadeia SEM regime sumir do macro; nó que zerar aparece como
  // idle "—", que noDoGrupo já renderiza.
  const linhasMacro = useMemo(() => {
    const linhas = esteira?.linhas ?? [];
    return linhas
      .map(linha => ({
        ...linha,
        grupos: linha.grupos
          .map(g => {
            const lista = listaDoGrupoFiltrada(g);
            return { ...g, contagens: contagensDaLista(lista), total_empresas: lista.length };
          })
          .filter(g => !filtroAtivo || g.total_empresas > 0),
      }))
      .filter(linha => linha.grupos.length > 0);
  }, [esteira, filtroAtivo, listaDoGrupoFiltrada]);

  // ── ORDEM DOS FLUXOS (13/10, pedido do dono: "preciso deixar os do fiscal no
  // topo, contábil abaixo, mas é melhor eu poder mesmo definir") ──────────────
  // A ordem é GLOBAL: fica no campo `ordem` do departamento (monitoramento) e
  // todo mundo vê a mesma. Sem `ordem` gravada vale o padrão: Fiscal, depois
  // Contábil, depois o resto. Gravar exige o mesmo perfil que edita a aba
  // Dependências (admin/desenvolvimento) — quem não tem vê a ordem, e a
  // tentativa de mudar vale só na sessão, com aviso.
  const queryClient = useQueryClient();
  /** Ordem otimista enquanto grava (ou que ficou só na sessão se a gravação falhou). */
  const [ordemLocal, setOrdemLocal] = useState<string[] | null>(null);
  const [avisoOrdem, setAvisoOrdem] = useState<string | null>(null);
  const departamentoPorNome = useMemo(
    () => new Map((departamentosConfig ?? []).map(d => [d.nome, d])),
    [departamentosConfig],
  );
  // A regra mora em `utils/ordemDoFluxo` desde 19/10: Resumo e Análise usam a
  // MESMA, para nenhuma aba ordenar diferente da que o dono organizou aqui.
  const fluxosNaOrdem = useMemo(() => {
    const nomes: string[] = [];
    linhasMacro.forEach(l => {
      const nome = l.departamento ?? '';
      if (!nomes.includes(nome)) nomes.push(nome);
    });
    return ordenarFluxos(nomes, departamentosConfig, ordemLocal);
  }, [linhasMacro, ordemLocal, departamentosConfig]);
  /** Só fluxo com departamento cadastrado pode ser movido — é onde a ordem mora. */
  const fluxosMoviveis = useMemo(
    () => fluxosNaOrdem.filter(nome => departamentoPorNome.has(nome)),
    [fluxosNaOrdem, departamentoPorNome],
  );
  const linhasMacroOrdenadas = useMemo(
    () =>
      linhasMacro
        .map((linha, i) => ({ linha, i }))
        .sort(
          (a, b) =>
            fluxosNaOrdem.indexOf(a.linha.departamento ?? '') -
              fluxosNaOrdem.indexOf(b.linha.departamento ?? '') || a.i - b.i,
        )
        .map(x => x.linha),
    [linhasMacro, fluxosNaOrdem],
  );
  const moverFluxo = async (nome: string, delta: -1 | 1) => {
    const lista = fluxosMoviveis.slice();
    const i = lista.indexOf(nome);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= lista.length) return;
    [lista[i], lista[j]] = [lista[j], lista[i]];
    setOrdemLocal(lista);
    setAvisoOrdem(null);
    try {
      // Grava a posição de TODOS os que mudaram (não só o par trocado): na
      // primeira vez ninguém tem `ordem`, e gravar só dois deixaria os demais
      // sem posição, flutuando acima ou abaixo conforme o padrão.
      await Promise.all(
        lista.map((nomeFluxo, posicao) => {
          const departamento = departamentoPorNome.get(nomeFluxo);
          if (!departamento || departamento.ordem === posicao) return null;
          return atualizarDepartamento(departamento.id, { ordem: posicao });
        }),
      );
      await queryClient.invalidateQueries({ queryKey: ['orqDepDepartamentos'] });
      setOrdemLocal(null);
    } catch {
      // A ordem escolhida continua valendo NESTA sessão (`ordemLocal` fica), mas
      // a tela diz que não foi gravada — senão o usuário acharia que mudou para
      // todos.
      setAvisoOrdem(
        'A ordem não foi gravada para todos (precisa de perfil administrador, ou o servidor está somente-leitura). Ela vale só nesta sessão.',
      );
    }
  };

  // ── Board das sentinelas: bots de /dependencias/bots + contagens do /resumo ─
  const botsSentinela: IBotBoard[] = useMemo(() => {
    if (!ehSentinela || !vinculos) return [];
    // "Bots sem grupo" = grupo_ids VAZIO (bot em N grupos aparece em cada grupo real,
    // nunca aqui); "Todos os bots" lista cada bot 1 vez, independente dos grupos.
    const selecionados =
      grupoSel === SEM_GRUPO_ID ? vinculos.filter(v => v.grupo_ids.length === 0) : vinculos;
    return selecionados.map(v => ({
      bot: v.bot,
      tipo_bot: '',
      dep_bot: v.dep_bot,
      contagens: contagensDoBot(empresas, v.bot),
      total_empresas: empresasDoBotComStatus(empresas, v.bot).length,
      ultima_execucao: null,
      em_execucao: execDoBot.has(v.bot),
      exec_id: execDoBot.get(v.bot) ?? null,
    }));
  }, [ehSentinela, vinculos, grupoSel, empresas, execDoBot]);

  const botsDoBoardBase = grupoRealSel !== null ? (boardGrupo?.bots ?? []) : botsSentinela;
  const botsDoBoard = useMemo(() => {
    const rederivados = botsDoBoardBase.map(b => {
      const lista = listaDoBotFiltrada(b.bot);
      return { ...b, contagens: contagensDaLista(lista), total_empresas: lista.length };
    });
    // Pruning SÓ com filtro global ativo (correção M2 — ver linhasMacro): com
    // apenas regime por nível, bot que zerar fica visível como idle "—".
    return filtroAtivo ? rederivados.filter(b => b.total_empresas > 0) : rederivados;
  }, [botsDoBoardBase, filtroAtivo, listaDoBotFiltrada]);
  const linhasBots = useMemo(() => montarLinhasBots(botsDoBoard), [botsDoBoard]);

  // ── Nós (node() do protótipo, sobre contagens reais) ───────────────────────
  /**
   * Nó de GRUPO (macro): meta COM ⏳ waiting (correção 03/09) e com o alerta
   * SOMADO NO ✕, sem símbolo próprio (25/09 — pedido do dono: "não vai ter mais
   * o sinal de aviso (...) mas ele vai estar contando no ✕ junto").
   *
   * É a mesma fusão da pílula "Falha de processamento": o rodapé era o último
   * lugar da macro que ainda separava `err` de `warn`, e exibir `✕ 61 · ⚠ 147`
   * ao lado de uma pílula que soma os dois mostrava o mesmo fato com duas
   * réguas. O `⚠` continua no MICRO e no board, onde se investiga um bot.
   *
   * A BARRA ACOMPANHA O TEXTO (05/10, pedido do dono): o alerta entra no VERMELHO,
   * e sai do verde. Até 04/10 era o contrário — `warn` contava como avanço
   * (`passedBarra = done + warn`) com a justificativa de que não trava a rota e
   * "o percentual verde não pode regredir".
   *
   * O que derrubou essa justificativa foi a medição de 05/10: em "Apuração LP" há
   * 8 `err` contra 115 `warn`, então a barra ficava 68% VERDE e 2% vermelha
   * enquanto o rodapé logo abaixo dizia `✕ 123` e a taxa dizia 50% de sucesso. A
   * mesma empresa era falha em três lugares e sucesso num — contradição dentro do
   * mesmo nó, que foi exatamente o que o dono apontou no print.
   *
   * Agora as quatro réguas do nó concordam: barra, rodapé, taxa de sucesso e
   * pílula "Falha de processamento" tratam `err + warn` como o mesmo grupo.
   *
   * A TAG continua em `errs` puro, de propósito: ela conta "empresas travadas", e
   * alerta não trava a rota — a empresa seguiu para a etapa seguinte.
   */

  /** Abre o popup analítico (abas por RPA + parcelas) — ver `PopupProcessos`. */
  const abrirPopupPainel = (
    base: Pick<PopupDados, 'title' | 'titleTooltip' | 'sub' | 'actionLabel' | 'onAction'>,
    abas: AbaDoPainel[],
    categoria?: CategoriaPainel | 'naoSeguiram',
    erroParcial?: string[],
    ladoMov?: 'com' | 'sem',
  ) => {
    const lista = abas[0]?.companies ?? [];
    setPopup({
      ...base,
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      painel: {
        abas,
        nomes: nomesDasEmpresas,
        competencia: rotuloMovimento,
        categoriaInicial: categoria ?? null,
        movimentoInicial: ladoMov ?? null,
        comMovimento,
        erroParcial: erroParcial ?? [],
      },
    });
  };

  /**
   * CARTÃO DA ETAPA com painel (13/10). Substitui o nó antigo sempre que o
   * universo nomeado da área está disponível; sem ele `noDoGrupo` segue no
   * desenho anterior, que é a única resposta possível sem denominador.
   */
  const cartaoDoGrupo = (
    g: IGrupoEsteira,
    anterior: IGrupoEsteira | undefined,
    area: IAreaCobertura | undefined,
    montado: NonNullable<ReturnType<typeof painelDoGrupo>>,
  ): NoDados => {
    const p = montado.painel;
    const montadoAnterior = anterior ? painelDoGrupo(anterior, area) : null;
    const funil = montadoAnterior ? funilDoNo(anterior!.nome, montadoAnterior.painel, p) : null;
    // ERRO PARCIAL (13/10): na etapa com vários RPAs, um erro em UM deles já põe
    // a empresa em erro. Separar quem concluiu em parte dos RPAs de quem falhou
    // em todos diz o tamanho real do problema.
    const okEmAlgumRpa = new Set(montado.porBot.flatMap(b => b.painel.ok));
    const erroParcial = g.bots.length > 1 ? p.erro.filter(c => okEmAlgumRpa.has(c)) : [];
    const abas: AbaDoPainel[] = [
      {
        id: '__etapa',
        rotulo: g.bots.length > 1 ? 'Etapa inteira' : g.nome,
        painel: p,
        companies: montado.lista,
        funil,
      },
      ...(g.bots.length > 1
        ? montado.porBot.map(b => ({
            id: b.bot,
            rotulo: apelidoDoBot(b.bot),
            titleTooltip: temApelido(b.bot) ? b.bot : undefined,
            painel: b.painel,
            companies: b.lista,
            funil: null,
          }))
        : []),
    ];
    const abrir = (categoria?: CategoriaPainel | 'naoSeguiram', ladoMov?: 'com' | 'sem') =>
      abrirPopupPainel(
        {
          title: g.nome,
          sub: anterior ? `Depende de ${anterior.nome}` : 'Sem dependência — roda em paralelo',
          actionLabel: 'Ver RPAs desta etapa →',
          onAction: () => abrirBoard(g.id ?? SEM_GRUPO_ID),
        },
        abas,
        categoria,
        erroParcial,
        ladoMov,
      );
    const pct = (n: number) => (p.base ? (n / p.base) * 100 : 0);
    return montarNo({
      circleText: circuloDoPainel(p),
      tone: tomDoPainel(p),
      tag: `${p.base} empresas no regime`,
      label: g.nome,
      selo: g.bots.length === 1 ? '1 RPA' : `${g.bots.length} RPAs`,
      seloTitle: g.bots.map(b => apelidoDoBot(b)).join(' · '),
      barPct: pct(p.ok.length),
      barErrPct: pct(p.erro.length),
      barWarnPct: pct(p.andamento.length),
      barSkipPct: pct(p.semMovimento.length),
      meta: '',
      selected: false,
      onSelect: () => abrir(),
      parcelas: parcelasDoCartao(p, funil, abrir, erroParcial.length),
    });
  };

  const noDoGrupo = (
    g: IGrupoEsteira,
    anterior: IGrupoEsteira | undefined,
    area?: IAreaCobertura,
  ): NoDados => {
    // Grupo que processa ITENS (solicitações), não empresas, fica no desenho
    // antigo: repartir o universo de empresas por ele não faria sentido.
    const montado = coberturaDoGrupo(g, area)?.cobertura_itens ? null : painelDoGrupo(g, area);
    if (montado) return cartaoDoGrupo(g, anterior, area, montado);
    // `warn` não é desestruturado aqui de propósito (05/10): na macro ele nunca
    // aparece sozinho — entra no `✕` e no vermelho da barra, os dois via
    // `falhasDaMacro`. Ter a variável solta convidava a usá-la como terceira
    // régua, que é exatamente o que esta correção acabou de eliminar.
    const { done, err: errs } = g.contagens;
    // O ✕ do rodapé na macro: err + warn (ver `falhasDaMacro`). Desde 05/10 é
    // TAMBÉM o vermelho da barra — o mesmo número nos dois lugares.
    const falhasMeta = falhasDaMacro(g.contagens);
    // "N não chegaram a esta etapa" (07/10): quantas empresas do FLUXO ainda não
    // apareceram neste agrupamento — o pedido literal do dono: *"temos 100
    // empresas iniciais no primeiro agrupamento, só que quando eu chego no
    // último rodaram apenas 40-60, e eu não faço ideia o que aconteceu com o
    // restante"*. Sai de `/cobertura`, a MESMA fonte da bolha de porcentagem
    // logo acima, para o número novo não discordar do que está ao lado dele.
    const doGrupoCob = coberturaDoGrupo(g, area);
    const naoChegaram = naoChegaramAoGrupo(doGrupoCob, area);
    // As três parcelas do rodapé, pela MESMA régua da bolha — ver `parcelasDoNo`.
    // `null` sem cobertura: aí o rodapé cai no `/resumo` de antes, que é uma
    // régua diferente mas é a única resposta disponível, e some junto a garantia
    // de fechamento (o nó não finge uma identidade que não pode provar).
    // As três parcelas da régua do dono: sucesso + erro + não chegou === base.
    // `falhasMeta` (err + warn) é o "erro OU warning soma com erro" dele.
    // O `↳` mede sobre o UNIVERSO DO DEPARTAMENTO (regra do dono, 09/10):
    // "o L é apenas pras que não rodaram no agrupamento com base no total de
    // empresas do universo". `area.universo` é esse total — 345 no Fiscal LP,
    // contra os 344 da união dos grupos que a base anterior usava.
    const parcelas = parcelasDoNo(naoChegaram, falhasMeta, area?.universo);
    // Verde = só quem CONCLUIU. O alerta saiu daqui e foi para o vermelho.
    const passedBarra = done;
    const running = g.contagens.run;
    const waiting = g.contagens.wait;
    // PENDENTES (25/09): o bot decidiu nao processar agora. Ganham fatia AZUL na
    // barra — sem ela ficavam no cinza do fundo, indistinguiveis de quem ainda
    // nao rodou, e sao coisas diferentes: uma o bot ja' decidiu, a outra nao.
    const pendentes = g.contagens.skip;
    const total = g.total_empresas;
    const paradas = errs + running;
    // `waiting + running`: quem falhou ja' foi processada e nao esta' na fila.
    const { circleText, tone } = circuloDaFila(waiting + running, total);
    // Percentuais da barra: pela régua nova quando há cobertura, pelo `/resumo`
    // de antes quando não há. Extraídos daqui de dentro do `montarNo` porque
    // dois ternários aninhados ali eram ilegíveis (e o lint recusa).
    const pctDe = (parte: number, base: number) => (base ? Math.round((parte / base) * 100) : 0);
    const pctVerde = pctDe(passedBarra, total);
    // O VERMELHO VOLTA PARA `/resumo` (08/10, 2ª rodada).
    //
    // A unificação da manhã puxou o ✕ para o `/cobertura` e ele ZEROU em todos
    // os nós — "100% de sucesso" e "sem empresas paradas" na tela inteira, com
    // o erro sumido. Medido na fonte (`cobertura_service.py:713`): o fecho de
    // unidade entra em `passaram` E em `entraram`, então pela cobertura toda
    // empresa com log no nó concluiu nele, e `naoConcluiram` é ~0 por
    // construção. O `/cobertura` simplesmente NÃO SABE de erro.
    //
    // Cada número com a fonte que responde a pergunta dele: quem falhou sai do
    // `/resumo` (que tem desfecho), quem não chegou sai do `/cobertura` (que
    // tem as listas). Não é régua duplicada — são perguntas diferentes, e a
    // tela diz qual é qual.
    const pctVerdeNo = parcelas ? pctDe(parcelas.sucesso, parcelas.base) : pctVerde;
    const pctVermelho = parcelas ? pctDe(parcelas.erro, parcelas.base) : pctDe(falhasMeta, total);
    return montarNo({
      circleText,
      tone,
      // A TAG segue o ✕ do rodapé — as duas falam de FALHA, e falha vive no
      // `/resumo`. Com a cobertura elas zeravam juntas ("sem empresas paradas"
      // num nó com erro real).
      // A TAG segue o ✕ do rodapé — as duas falam de FALHA.
      tag: parcelas ? tagDoNo(parcelas.erro, parcelas.erro + running) : tagDoNo(errs, paradas),
      label: g.nome,
      // `null` quando a cobertura não respondeu: sem base a pergunta não tem
      // resposta, e zero afirmaria "todas chegaram".
      naoChegaram: naoChegaram
        ? {
            // SÓ `semLog` (pedido do dono, 08/10): *"vamos mostrar apenas as
            // que não processaram ali... pra não confundir mais as coisas e
            // contar a mesma empresa em dois lugares"*. Quem chegou e não
            // concluiu já é o ✕ do rodapé; somá-la aqui contava a mesma empresa
            // em duas linhas do mesmo nó — a origem do `+88`, `+144`, `+111`
            // que ele conferiu na calculadora.
            //
            // Com isto, ✓ + ✕ + ↳ === universo, pela mesma fonte.
            // SÓ quem não tem log aqui (pedido do dono): quem errou é o ✕, e
            // contá-la nos dois somaria a mesma empresa duas vezes.
            total: parcelas ? parcelas.naoChegou : naoChegaram.semLog.length,
            semLog: naoChegaram.semLog.length,
            naoConcluiram: naoChegaram.naoConcluiram.length,
            baseLabel: naoChegaram.baseLabel,
            truncado: naoChegaram.truncado,
          }
        : null,
      // A BARRA segue o rodapé (05/10: "o mesmo número nos dois lugares"), então
      // ela acompanha a régua nova quando há cobertura.
      barPct: pctVerdeNo,
      barErrPct: pctVermelho,
      barSkipPct: total ? Math.round((pendentes / total) * 100) : 0,
      barColor: C.green,
      // RODAPÉ PELA MESMA RÉGUA DA BOLHA (08/10, pedido do dono).
      //
      // > *"o valor somado das empresas que deram certo e as que deram errado
      // > mais as skip e mais as que não passaram, ou dá um valor menor ou muito
      // > superior ao valor total... igual o exemplo de 344 de 345 (falta 1), e
      // > 489 de 345 empresas (passando 144 do total)"*
      //
      // Até 07/10 o ✓/✕ saía de `g.contagens` (lista do `/resumo`, contada por
      // EXECUÇÃO) e a bolha do `/cobertura` (contada por EMPRESA). Medido no
      // print dele, em Fiscal LP: bolha 255 contra rodapé 344 no mesmo nó — e
      // nos dois nós em que as fontes concordavam a conta fechava, que é o
      // "pra alguns cálculos dá certo e pra outros não".
      //
      // Agora as três parcelas saem de `parcelasDoNo`, onde são partições
      // DISJUNTAS do mesmo conjunto: ✓ + ✕ + ↳ === universo, por construção.
      //
      // Fila e pendentes seguem no rodapé: são estado dentro de quem CHEGOU
      // (já contado em ✓/✕), e não parcelas novas da soma — tirá-los esconderia
      // trabalho em andamento que o nó precisa mostrar.
      // ✓ CONCLUÍDAS pela cobertura (a mesma fonte da bolha logo acima, para os
      // dois números não se contradizerem) e ✕ FALHAS pelo `/resumo`, que é a
      // única rota que sabe de desfecho — ver `pctVermelho` acima.
      meta: emojiMeta(
        parcelas ? parcelas.sucesso : done,
        parcelas ? parcelas.erro : falhasMeta,
        0,
        running + waiting,
        undefined,
        undefined,
        undefined,
        { skip: pendentes },
      ),
      // TAXA DE SUCESSO (30/09): concluídas sobre as que já tiveram desfecho — e
      // pelos MESMOS números do rodapé logo abaixo, senão a taxa o contradiz.
      // Pelos MESMOS números do rodapé logo abaixo, senão a taxa o contradiz.
      pctSucesso: parcelas
        ? pctSucesso(parcelas.sucesso, parcelas.erro)
        : pctSucesso(done, falhasMeta),
      selected: false,
      // A derivação vai PRONTA para o popup: ele é dumb (não sabe qual nó o
      // abriu), e recalcular lá criaria uma segunda régua para a mesma conta.
      onSelect: () => abrirPopupGrupo(g, anterior, naoChegaram, parcelas),
      // BOLHA DE PORCENTAGEM do nó de grupo (17/09).
      //
      // O grupo agrega VÁRIOS bots, e a cobertura do grupo é a UNIÃO das
      // coberturas deles — união que só o backend sabe fazer sem contar empresa
      // repetida duas vezes. Por isso a bolha do grupo só aparece quando o grupo
      // tem UM bot: aí a cobertura do grupo É a do bot, sem união nenhuma.
      //
      // Com vários bots a bolha fica ausente em vez de exibir uma soma que
      // inflaria o número (empresa que passa em 2 bots do grupo contaria 2x) —
      // exatamente a duplicidade que o pedido manda eliminar. O percentual da
      // ÁREA, que é a união correta, continua visível no título da linha.
      //
      // Também aqui a bolha deixou de sumir (18/09): grupo com N bots continua
      // SEM percentual (a união é do backend, somar aqui contaria a empresa que
      // passa em 2 bots duas vezes), mas agora diz isso em vez de desaparecer.
      acima: (() => {
        if (!area) {
          return bolhaIndisponivel(g.nome, 'área sem cobertura conhecida');
        }
        // COBERTURA DO GRUPO (21/09): o backend passou a publicar a união
        // DEDUPLICADA dos bots de cada grupo, então o nó multi-bot voltou a ter
        // número. Antes, `g.bots.length !== 1` mandava todo grupo com 2+ bots
        // (ou nenhum) para o "—" — e eram justamente os nós mais movimentados
        // ("Transmissão dos Impostos LP/SN", com centenas de empresas na linha
        // logo abaixo). Somar aqui continua PROIBIDO: a empresa que passa em
        // dois bots do grupo contaria duas vezes e o percentual estouraria; só
        // o servidor tem quem passou (conjunto), não quantos (contador).
        // `g.id` é null no grupo virtual "Bots sem grupo" — ele não existe no
        // cadastro, então não tem cobertura publicada e cai no caminho abaixo.
        // `g.id` null: o grupo virtual dos bots sem vinculo. Desde 25/09 o
        // backend PUBLICA cobertura para eles (area com `departamento_id: null`),
        // entao ele deixou de cair no "—" — o `?? undefined` mantem o caminho
        // antigo vivo para payload de mon anterior.
        const doGrupo = g.id
          ? indexarGrupos(area).get(g.id)
          : (area.grupos || []).find(x => x.grupo_id === null);
        if (doGrupo) {
          // COBERTURA POR ITEM (25/09): o grupo que nao processa empresa mede o
          // que ele de fato processa. Sem isto o no' ficava em `·` ("sem
          // execucao") tendo rodado 27 solicitacoes — o backend publica o campo
          // so' quando a cobertura de empresa nao responde nada.
          const fatia = doGrupo.cobertura_itens ?? doGrupo.cobertura;
          const porItem = Boolean(doGrupo.cobertura_itens);
          return {
            texto: porItem
              ? textoDaBolha(fatia)
              : bolhaDoNo(doGrupo.cobertura, doGrupo.entraram, doGrupo.reporta_modulos),
            title: porItem
              ? `${g.nome}: ${tituloDaBolha(fatia, 'itens processados')} — este bot processa itens (solicitações), não empresas`
              : tituloDoNo(
                  doGrupo.cobertura,
                  doGrupo.entraram,
                  g.nome,
                  doGrupo.reporta_modulos,
                  doGrupo.bots.length,
                ),
            formato: 'pct' as const,
            // QUANTAS empresas passaram por este no' (25/09). `entraram`, nao
            // `cobertura`: a pergunta e' "quantas passam ali dentro", e quem
            // entrou passou pelo no' mesmo que ainda nao tenha concluido. O
            // `cobertura.quantidade` responderia "quantas concluiram", que e' o
            // que o percentual ja' diz.
            quantidade: porItem ? fatia.quantidade : (doGrupo.entraram?.quantidade ?? null),
            // O DENOMINADOR sobe do rodape para a bolha (25/09). Vem da propria
            // fatia — a mesma fonte que o tooltip ja' usa —, entao a frase da
            // bolha e a do hover nao podem divergir.
            total: (porItem ? fatia : doGrupo.entraram)?.denominador ?? null,
            onSelect: () => abrirFatiaCobertura(`Cobertura de ${g.nome}`, fatia, g.nome),
          };
        }
        // Sem o grupo no payload (mon/BFF ainda sem o campo, ou grupo sem id no
        // cadastro): cai no caminho antigo, que resolve o grupo de UM bot. É
        // degradação explícita, não silenciosa — e o de N bots volta ao "—"
        // explicado em vez de um número inventado.
        if (g.bots.length !== 1) {
          return bolhaIndisponivel(
            g.nome,
            `este grupo tem ${g.bots.length} bots e a união deles não é somável aqui — use o percentual da área`,
          );
        }
        const micro = indexarBots(area).get(g.bots[0]);
        if (!micro) {
          return bolhaIndisponivel(g.nome, 'bot do grupo sem cobertura conhecida');
        }
        return {
          texto: bolhaDoNo(micro.cobertura, micro.entraram),
          title: tituloDoNo(micro.cobertura, micro.entraram, `${g.nome} (bot do grupo)`),
          formato: 'pct' as const,
          quantidade: micro.entraram?.quantidade ?? null,
          total: micro.entraram?.denominador ?? null,
          onSelect: () =>
            abrirFatiaCobertura(
              `Cobertura de ${g.nome}`,
              micro.cobertura,
              `${g.nome} (bot do grupo)`,
            ),
        };
      })(),
    });
  };

  /**
   * Nó de BOT (board): sem ⏳ no meta (comportamento do protótipo) e COM ⚠ alerta
   * (correção 12/09 — mesma regra do nó de grupo: o warn deixou de ser somado
   * dentro do ✓, senão o alerta não existe como categoria em nível nenhum).
   *
   * BARRA (05/10): o alerta sai do VERDE e ganha fatia AMBAR própria. Aqui ele
   * não vai para o vermelho como na macro, porque neste nível o `⚠` tem número
   * próprio no rodapé — a barra pinta exatamente o que o texto escreve. O que
   * acabou foi o alerta contar como avanço: `passed` era `done + warn`, e era
   * isso que fazia a barra dizer "quase tudo verde" num nó cujo rodapé mostrava
   * dezenas de alertas.
   *
   * `total` NÃO muda: o alerta sempre esteve no denominador (via `passed`), e
   * continua — agora pela parcela própria.
   */
  /** CARTÃO DO RPA com painel (13/10) — as mesmas parcelas da etapa, por bot. */
  const cartaoDoBot = (
    b: IBotBoard,
    anterior: IBotBoard | undefined,
    montado: NonNullable<ReturnType<typeof painelDoBot>>,
  ): NoDados => {
    const p = montado.painel;
    const montadoAnterior = anterior ? painelDoBot(anterior.bot) : null;
    const funil = montadoAnterior
      ? funilDoNo(apelidoDoBot(anterior!.bot), montadoAnterior.painel, p)
      : null;
    const abas: AbaDoPainel[] = [
      {
        id: b.bot,
        rotulo: apelidoDoBot(b.bot),
        titleTooltip: temApelido(b.bot) ? b.bot : undefined,
        painel: p,
        companies: montado.lista,
        funil,
      },
    ];
    const abrir = (categoria?: CategoriaPainel | 'naoSeguiram', ladoMov?: 'com' | 'sem') =>
      abrirPopupPainel(
        {
          title: apelidoDoBot(b.bot),
          titleTooltip: temApelido(b.bot) ? b.bot : undefined,
          sub: anterior
            ? `Depende de ${apelidoDoBot(anterior.bot)}`
            : 'Sem dependência — roda em paralelo',
          actionLabel: 'Ver etapas internas →',
          onAction: () => abrirNano(b.bot, b.exec_id),
        },
        abas,
        categoria,
        undefined,
        ladoMov,
      );
    const pct = (n: number) => (p.base ? (n / p.base) * 100 : 0);
    return montarNo({
      circleText: circuloDoPainel(p),
      tone: tomDoPainel(p),
      tag: `${p.base} empresas no regime`,
      label: apelidoDoBot(b.bot),
      labelTitle: temApelido(b.bot) ? b.bot : undefined,
      selo: b.em_execucao ? 'RPA · rodando' : 'RPA',
      barPct: pct(p.ok.length),
      barErrPct: pct(p.erro.length),
      barWarnPct: pct(p.andamento.length),
      barSkipPct: pct(p.semMovimento.length),
      meta: '',
      selected: false,
      onSelect: () => abrir(),
      parcelas: parcelasDoCartao(p, funil, abrir),
    });
  };

  const noDoBot = (b: IBotBoard, anterior: IBotBoard | undefined): NoDados => {
    const montado = painelDoBot(b.bot);
    if (montado) return cartaoDoBot(b, anterior, montado);
    // MICRO (07/10): a base é o BOT DO QUAL ELE DEPENDE — quem concluiu lá e
    // não apareceu aqui. Só com `dep_bot` cadastrado, por pedido explícito do
    // dono: *"comparando o bot com bot somente se eles estiverem interligados
    // com dependência"*. Sem ela o indicador não aparece.
    const naoChegaram = naoChegaramNesteBot(b.bot, b.dep_bot);
    const { done, warn: warns, err: errs } = b.contagens;
    // Verde = só quem CONCLUIU (05/10). O alerta tem fatia âmbar própria.
    const passed = done;
    const running = b.contagens.run;
    const waiting = b.contagens.wait;
    const pendentes = b.contagens.skip;
    const paradas = errs + running;
    // `waiting` ENTRA no total (24/09): o circulo diz quantas faltam, e uma
    // empresa na fila e' exatamente isso. Sem ela no denominador o no' mostraria
    // "✓" com gente esperando — afirmaria fim de trabalho que nao acabou.
    //
    // `pendentes` tambem (25/09), pela mesma razao: elas passaram pelo bot (ele
    // decidiu nao processar), entao fazem parte do que o no' mede. Fora do total
    // a fatia azul seria calculada sobre um denominador que a exclui.
    // `warns` entra explicitamente: saiu de dentro de `passed` (05/10) e o
    // denominador não pode encolher — ele mede o que o nó processa.
    const total = paradas + passed + warns + waiting + pendentes;
    const { circleText, tone } = circuloDaFila(waiting + running, total);
    return montarNo({
      circleText,
      tone,
      tag: tagDoNo(errs, paradas),
      label: apelidoDoBot(b.bot),
      labelTitle: temApelido(b.bot) ? b.bot : undefined,
      barPct: total ? Math.round((passed / total) * 100) : 0,
      barErrPct: total ? Math.round((errs / total) * 100) : 0,
      // Fatia ÂMBAR do alerta (05/10) — própria deste nível, ver `barWarnPct`.
      barWarnPct: total ? Math.round((warns / total) * 100) : 0,
      barSkipPct: total ? Math.round((pendentes / total) * 100) : 0,
      barColor: C.green,
      // Sem `total` (25/09), como na macro: o denominador vive na bolha agora.
      // O ⚠ CONTINUA aqui — a fusao no ✕ e' so' da macro.
      meta: emojiMeta(done, errs, running, undefined, undefined, undefined, warns, {
        skip: pendentes,
      }),
      // TAXA DE SUCESSO (30/09): aqui o denominador é `errs` PURO, porque no
      // board o ⚠ tem parte própria no rodapé e não está somado no ✕. Alerta não
      // é sucesso (decisão do dono) e também não é falha neste nível, então fica
      // fora das duas pontas — como pendente, fila e execução.
      pctSucesso: pctSucesso(done, errs),
      selected: false,
      // Só publica quando há dependência cadastrada e cobertura dos dois bots.
      naoChegaram: naoChegaram
        ? {
            // SÓ `semLog` (pedido do dono, 08/10): *"vamos mostrar apenas as
            // que não processaram ali... pra não confundir mais as coisas e
            // contar a mesma empresa em dois lugares"*. Quem chegou e não
            // concluiu já é o ✕ do rodapé; somá-la aqui contava a mesma empresa
            // em duas linhas do mesmo nó — a origem do `+88`, `+144`, `+111`
            // que ele conferiu na calculadora.
            //
            // Com isto, ✓ + ✕ + ↳ === universo, pela mesma fonte.
            total: naoChegaram.semLog.length,
            semLog: naoChegaram.semLog.length,
            naoConcluiram: naoChegaram.naoConcluiram.length,
            baseLabel: naoChegaram.baseLabel,
            truncado: naoChegaram.truncado,
          }
        : null,
      onSelect: () => abrirPopupBot(b, anterior, naoChegaram),
      // BOLHA DO NÍVEL MICRO: o denominador é o da ÁREA-MÃE, não o do bot —
      // é o que torna os nós de uma mesma área comparáveis entre si ("este bot
      // cobriu 40% da área, aquele 90%"). O tooltip diz o denominador, sempre.
      //
      // A BOLHA NUNCA SOME (correção 18/09). Antes, `micro` ausente devolvia
      // `null` e o nó ficava SEM bolha nenhuma — foi assim que "Transmissão dos
      // Impostos LP" apareceu sem porcentagem tendo 277 empresas, ao lado de
      // "Geração de Guias LP" com 86%. Ausência de bolha é indistinguível de
      // "ainda não carregou" e não diz ao usuário o que houve; um estado
      // explícito diz.
      acima: (() => {
        const micro = microDoBotSelecionado(b.bot);
        if (!micro) {
          return bolhaIndisponivel(apelidoDoBot(b.bot), motivoSemMicro(b.bot));
        }
        return {
          texto: bolhaDoNo(micro.cobertura, micro.entraram),
          title: tituloDoNo(micro.cobertura, micro.entraram, apelidoDoBot(b.bot)),
          formato: 'pct' as const,
          // MICRO (no' de bot): mesma regra da macro — quantas empresas passaram
          // por ESTE bot. O pedido cobre os dois niveis.
          quantidade: micro.entraram?.quantidade ?? null,
          total: micro.entraram?.denominador ?? null,
          onSelect: () =>
            abrirFatiaCobertura(
              `Cobertura de ${apelidoDoBot(b.bot)}`,
              micro.cobertura,
              apelidoDoBot(b.bot),
            ),
        };
      })(),
    });
  };

  // ── Nano: módulos reais da execução ────────────────────────────────────────
  const modulosNano = useMemo(
    () => (nano?.modulos ?? []).slice().sort((a, b) => a.modulo_id - b.modulo_id),
    [nano],
  );

  // ── Ocupação do nano (fix 08/09, causa raiz em derivarNano.ts): a posição das
  // empresas em voo NÃO depende mais só do `atual` — quando o mon manda
  // `atual: null` com a fila cheia (empresa em voo via unidade aberta, sem evento
  // de módulo aberto), elas caem no primeiro módulo `em_andamento` ou no balde
  // visível "Etapa não informada", nunca somem da tela. ─────────────────────────
  const ocupacao = useMemo(
    () =>
      ocupacaoEmVoo(modulosNano, nano?.atual ?? null, filaExec ? filaExec.em_processamento : null),
    [modulosNano, nano, filaExec],
  );
  /** Empresas do balde "Etapa não informada", já respeitando o filtro de status. */
  const bucketEmVoo = stOk('run') ? ocupacao.semEtapa : 0;

  const emVooLista = useMemo(
    () => (filaExec ? listaDeCodis(filaExec.em_processamento, 'run', filaExec.empresas) : []),
    [filaExec],
  );
  /**
   * Entregues no bot inteiro (ok + erro) — a dupla do popup de ENTREGA (última
   * etapa). A bolinha "já passaram" NÃO usa a dupla inteira: entregue com ERRO
   * fica fora de todos os módulos (`jaPassaramPeloModulo` descarta `st: 'err'`
   * — não sabemos em qual etapa a empresa falhou; correção A1 da revisão 09/09).
   */
  const entreguesNoBot = useMemo(
    () =>
      execDetalhe
        ? [
            ...listaDeCodis(execDetalhe.ok_empresas, 'done', filaExec?.empresas),
            ...listaDeCodis(execDetalhe.erro_empresas, 'err', filaExec?.empresas),
          ]
        : [],
    [execDetalhe, filaExec],
  );
  // Recorte global + regime do contexto sobre as listas vindas da EXECUÇÃO (bolinha
  // "já passaram") — regra de ouro: filtrar ANTES de agregar a contagem. Empresa que
  // o /resumo não conhece não é cortada (dado vivo da execução vs cache de 300s).
  const codisConhecidos = useMemo(() => new Set(empresas.map(e => e.codi_emp)), [empresas]);
  const codisPermitidosNoBot = useMemo(
    () =>
      botSel ? new Set(baseDoBotNoContexto(grupoRealSel)(botSel.bot).map(e => e.codi_emp)) : null,
    [botSel, baseDoBotNoContexto, grupoRealSel],
  );
  /**
   * Lista da bolinha do módulo i (número = lista, revisão 09/09): entregues-OK no
   * bot + em voo com âncora INDIVIDUAL conhecida adiante da etapa (a empresa que o
   * `atual` nomeia, ou a única em voo). Entregue com erro e em voo sem posição
   * conhecida ficam FORA — a nota no subtítulo do popup explica. Quem o /resumo
   * não conhece passa pela keep-rule e ganha a tag "fora do resumo".
   */
  const jaPassaramDoModulo = useCallback(
    (i: number) =>
      filtrarPorStatus(
        marcarForaDoResumo(
          filtrarPorRecorte(
            jaPassaramPeloModulo(
              i,
              ocupacao.indiceEmVoo,
              emVooLista,
              entreguesNoBot,
              nano?.atual?.codi_emp ?? null,
            ),
            codisPermitidosNoBot,
            codisConhecidos,
          ),
          codisConhecidos,
        ),
        filtros.status,
      ),
    [
      ocupacao,
      emVooLista,
      entreguesNoBot,
      nano,
      codisPermitidosNoBot,
      codisConhecidos,
      filtros.status,
    ],
  );

  /**
   * DRILL-DOWN da BOLHA de porcentagem: "87%" -> QUAIS são as 305 empresas.
   *
   * A lista entregue ao popup é `fatia.empresas` — o MESMO array de onde saiu a
   * contagem e o percentual (o backend garante `quantidade === empresas.length`).
   * Não há recontagem aqui: recontar abriria a porta para o nó dizer um número e o
   * popup listar outro, que é o bug histórico desta tela.
   *
   * O status é `wait` para todas, de propósito — igual ao drill-down da
   * distribuição: esta pergunta é "por onde a empresa passou", não "como ela
   * terminou". Misturar o vocabulário do fluxo com o do resultado (done/err/warn)
   * foi o que produziu `passed = done + warn`.
   */
  const abrirFatiaCobertura = (titulo: string, fatia: IFatiaCobertura, contexto: string) => {
    const nomePorCodi = new Map<string, string>();
    empresas.forEach(e => nomePorCodi.set(e.codi_emp, nomeEmpresa(e)));
    const lista: IEmpresaComStatus[] = fatia.empresas.map(codi => ({
      codiEmp: codi,
      nome: nomePorCodi.get(codi) ?? codi,
      st: 'wait' as StatusEsteira,
      motivo: null,
    }));
    setPopup({
      title: titulo,
      sub: tituloDaBolha(fatia, contexto),
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
    });
  };

  const abrirPopupModulo = (m: IModuloNano, i: number) => {
    const ehUltimo = i === modulosNano.length - 1;
    let lista: IEmpresaComStatus[] = [];
    if (ehUltimo && execDetalhe) {
      // Última etapa = ENTREGA: quem já concluiu o bot inteiro (decisão 13/08).
      lista = entreguesNoBot;
    } else if (ocupacao.indiceEmVoo === i && filaExec) {
      lista = emVooLista;
    }
    lista = filtrarPorStatus(lista, filtros.status);
    setPopup({
      title: nomeModulo(m),
      sub: `${botSel ? apelidoDoBot(botSel.bot) : ''} · etapa ${i + 1} de ${modulosNano.length}`,
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      // Nome REAL (o apelido é só o `sub`): a auditoria filtra pelo bot do cadastro.
      botPadrao: botSel?.bot,
    });
  };

  /** Popup da bolinha "já passaram" — mesmo padrão dos demais popups do nano. */
  const abrirPopupJaPassaram = (m: IModuloNano, i: number) => {
    const lista = jaPassaramDoModulo(i);
    setPopup({
      title: `Já passaram por ${nomeModulo(m)}`,
      // Nota das exclusões honestas (A1/M1, revisão 09/09): o número só afirma
      // passagem com evidência — o que fica fora precisa estar dito aqui.
      sub: `${botSel ? apelidoDoBot(botSel.bot) : ''} · adiante desta etapa ou já entregues no bot — não inclui empresas entregues com erro nem em voo sem posição conhecida`,
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      botPadrao: botSel?.bot,
    });
  };

  /** Popup do balde "Etapa não informada" — as em voo sem módulo identificável. */
  const abrirPopupSemEtapa = () => {
    const lista = filtrarPorStatus(emVooLista, filtros.status);
    setPopup({
      title: 'Etapa não informada',
      sub: `${botSel ? apelidoDoBot(botSel.bot) : ''} · em processamento, sem evento de módulo aberto`,
      companies: lista.length ? lista : null,
      count: lista.length ? null : 0,
      botPadrao: botSel?.bot,
    });
  };

  const noDoModulo = (m: IModuloNano, i: number): NoDados => {
    const ehUltimo = i === modulosNano.length - 1;
    // Contadores de EMPRESA respeitam o filtro de status: em processamento = 'run',
    // entregues (última etapa) = 'done' — assim o nó bate com o popup filtrado.
    // A posição em voo vem da ocupação derivada (atual → em_andamento → balde).
    const emProc = stOk('run') ? ocupacao.porIndice[i] : 0;
    const passagensVisiveis = stOk('done') ? m.passagens : 0;
    const entregues = ehUltimo ? passagensVisiveis : 0;
    let tone: 'run' | 'ok' | 'idle' = 'idle';
    if (emProc > 0) tone = 'run';
    else if (m.situacao === 'concluido') tone = 'ok';
    let circleText = '—';
    let tag = 'ninguém aqui agora';
    if (ehUltimo) {
      circleText = passagensVisiveis > 0 ? String(passagensVisiveis) : '—';
      tag = `${plural(passagensVisiveis, 'empresa entregue', 'empresas entregues')}`;
    } else if (emProc > 0) {
      circleText = String(emProc);
      tag = plural(emProc, 'empresa aqui', 'empresas aqui');
    } else if (m.passagens > 0) {
      circleText = '✓';
      tag = 'sem empresas paradas';
    }
    return montarNo({
      circleText,
      tone,
      tag,
      label: nomeModulo(m),
      barPct: m.situacao === 'concluido' ? 100 : 0,
      barErrPct: 0,
      barColor: C.green,
      meta: emojiMeta(entregues, 0, emProc, undefined, undefined, 'ninguém aqui agora'),
      // Como no protótipo: nó destacado quando há empresas nele (paradas agora ou,
      // na última etapa, já entregues).
      selected: emProc > 0 || (ehUltimo && passagensVisiveis > 0),
      onSelect: () => abrirPopupModulo(m, i),
      // Bolha ACIMA do círculo. Desde 17/09 ela mostra a PORCENTAGEM do funil
      // quando o backend conhece a etapa, e cai na contagem "já passaram"
      // (comportamento 08/09) quando não conhece.
      //
      // O DENOMINADOR DO NANO é quem entrou NAQUELA ETAPA — não o universo da
      // área. Com o denominador da área, um bot que atende 50 de 350 empresas
      // mostraria no máximo 14% em todos os módulos e a progressão interna, que é
      // o que o dono quer ver ("quantas passaram do primeiro pro segundo"),
      // ficaria ilegível.
      acima: (() => {
        const etapa = etapasDoBotSelecionado.get(m.modulo_id);
        if (etapa) {
          return {
            texto: textoDaBolha(etapa.avancaram),
            title: tituloDaBolha(etapa.avancaram, `${nomeModulo(m)} — avançaram desta etapa`),
            formato: 'pct' as const,
            onSelect: () =>
              abrirFatiaCobertura(
                `Avançaram de ${nomeModulo(m)}`,
                etapa.avancaram,
                `${nomeModulo(m)} — avançaram desta etapa`,
              ),
          };
        }
        // FALLBACK sem percentual (bot sem cobertura conhecida, ou em DUAS áreas
        // com denominadores diferentes): a bolha volta a ser a CONTAGEM de 08/09.
        // O tooltip diz explicitamente que é contagem, não percentual — trocar um
        // pelo outro sem avisar deixaria o usuário lendo "12" como "12%".
        return {
          texto: String(jaPassaramDoModulo(i).length),
          title: `Empresas que já passaram por ${nomeModulo(m)} (contagem, não percentual — este bot não tem denominador único)`,
          onSelect: () => abrirPopupJaPassaram(m, i),
        };
      })(),
    });
  };

  /** Nó do balde "Etapa não informada" — só existe com empresas em voo sem etapa. */
  const noSemEtapa = (): NoDados =>
    montarNo({
      circleText: String(bucketEmVoo),
      tone: 'run',
      tag: plural(bucketEmVoo, 'empresa aqui', 'empresas aqui'),
      label: 'Etapa não informada',
      barPct: 0,
      barErrPct: 0,
      barColor: C.green,
      meta: 'em processamento, sem evento de módulo aberto',
      selected: true,
      onSelect: abrirPopupSemEtapa,
    });

  // ── Lista lateral "Etapas do bot" (só no nano, como no protótipo) ──────────
  const grupoEtapas: GrupoLateral[] = useMemo(() => {
    if (nivel !== 'nano' || !modulosNano.length) return [];
    const rows = modulosNano.map((m, i) => {
      const ehUltimo = i === modulosNano.length - 1;
      // Mesmos contadores filtrados por status dos nós — a lista lateral nunca
      // diverge do board nem do popup (regra de ouro do protótipo). A posição em
      // voo vem da MESMA ocupação derivada dos nós (fix "ninguém aqui agora").
      const emProc = stOk('run') ? ocupacao.porIndice[i] : 0;
      const passagensVisiveis = stOk('done') ? m.passagens : 0;
      let st: StatusEsteira = 'wait';
      if (ehUltimo) st = passagensVisiveis > 0 ? 'done' : 'wait';
      else if (emProc > 0) st = 'run';
      const sub = ehUltimo
        ? `${plural(passagensVisiveis, 'empresa entregue', 'empresas entregues')} nesta etapa`
        : `${plural(emProc, 'empresa', 'empresas')} nesta etapa agora`;
      const temLista = emProc > 0 || (ehUltimo && passagensVisiveis > 0);
      return {
        key: String(m.modulo_id),
        name: nomeModulo(m),
        sub,
        st,
        m1: m.duracao_ms !== null ? fmtS(m.duracao_ms / 1000) : '—',
        m2: ehUltimo ? passagensVisiveis : emProc,
        m3: '—',
        action: temLista ? 'Empresas →' : '',
        onSelect: () => temLista && abrirPopupModulo(m, i),
      };
    });
    if (bucketEmVoo > 0) {
      // O balde entra também aqui: board, lateral e popup nunca divergem.
      rows.push({
        key: 'sem-etapa',
        name: 'Etapa não informada',
        sub: `${plural(bucketEmVoo, 'empresa', 'empresas')} em processamento sem etapa aberta`,
        st: 'run',
        m1: '—',
        m2: bucketEmVoo,
        m3: '—',
        action: 'Empresas →',
        onSelect: abrirPopupSemEtapa,
      });
    }
    return [
      {
        num: '1',
        name: 'Execução passo a passo',
        meta: `${modulosNano.length} etapas`,
        right: '',
        headBg: 'oklch(0.985 0.006 160)',
        rows,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, modulosNano, ocupacao, bucketEmVoo, stOk]);

  // ── Card "Fila" por nível (3 formatos — concluidosGroups() do protótipo) ───
  const filaDados: FilaDados = useMemo(() => {
    if (nivel === 'nano' && botSel) {
      if (filaExec) {
        // Como no protótipo, a Fila do nano lista TODAS as empresas do bot com
        // status — inclusive as que FALHARAM na execução (erro_empresas), que o
        // ordenarParaExibicao do FilaCard posiciona no TOPO da lista. Falha
        // primeiro no dedup: se um codi_emp aparecer também noutra lista, o erro
        // não pode ser mascarado.
        const vistos = new Set<string>();
        // Dedup ANTES do filtro de status: o status de cada empresa no contexto do
        // bot é o da 1ª lista em que aparece (falha primeiro) — o recorte de status
        // corta sobre esse status consolidado, igual aos popups.
        const flat = filtrarPorStatus(
          [
            ...listaDeCodis(execDetalhe?.erro_empresas ?? [], 'err', filaExec.empresas),
            ...listaDeCodis(filaExec.concluidas, 'done', filaExec.empresas),
            ...listaDeCodis(filaExec.em_processamento, 'run', filaExec.empresas),
            ...listaDeCodis(filaExec.aguardando, 'wait', filaExec.empresas),
          ].filter(c => {
            if (vistos.has(c.codiEmp)) return false;
            vistos.add(c.codiEmp);
            return true;
          }),
          filtros.status,
        );
        return {
          formato: 'flat',
          label: `${plural(flat.length, 'empresa passa', 'empresas passam')} por ${apelidoDoBot(
            botSel.bot,
          )}`,
          companies: flat,
        };
      }
      const doResumo = listaDoBotFiltrada(botSel.bot);
      return {
        formato: 'flat',
        label: `${plural(doResumo.length, 'empresa passa', 'empresas passam')} por ${apelidoDoBot(
          botSel.bot,
        )}`,
        companies: doResumo,
      };
    }

    if (nivel === 'board') {
      return {
        formato: 'grupos',
        label: '',
        // Chave de colapso segue o NOME REAL (`bot:`); só o rótulo usa o apelido.
        grupos: botsDoBoard.map(b => ({
          chave: `bot:${b.bot}`,
          nome: apelidoDoBot(b.bot),
          tituloReal: temApelido(b.bot) ? b.bot : undefined,
          companies: listaDoBotFiltrada(b.bot),
        })),
      };
    }

    // MACRO: faixas de Departamento (abertas) → grupos (fechados), dedup por empresa.
    // "Bots sem grupo" é sempre a última faixa, fora de qualquer departamento.
    const faixas: FilaFaixa[] = [];
    const gruposSemGrupo: FilaFaixa['grupos'] = [];
    linhasMacro.forEach(linha => {
      linha.grupos.forEach(g => {
        const agrupador = {
          chave: `grp:${g.id ?? SEM_GRUPO_ID}`,
          nome: g.nome,
          companies: listaDoGrupoFiltrada(g),
        };
        if (g.id === null) {
          gruposSemGrupo.push(agrupador);
          return;
        }
        const tituloFaixa = linha.departamento ?? 'Sem departamento';
        let faixa = faixas.find(f => f.nome === tituloFaixa);
        if (!faixa) {
          faixa = { chave: `dep:${tituloFaixa}`, nome: tituloFaixa, grupos: [] };
          faixas.push(faixa);
        }
        faixa.grupos.push(agrupador);
      });
    });
    if (gruposSemGrupo.length) {
      faixas.push({ chave: `dep:${SEM_GRUPO_ID}`, nome: 'Bots sem grupo', grupos: gruposSemGrupo });
    }
    return { formato: 'departamentos', label: '', faixas };
  }, [
    nivel,
    botSel,
    filaExec,
    execDetalhe,
    filtros.status,
    listaDoBotFiltrada,
    listaDoGrupoFiltrada,
    botsDoBoard,
    linhasMacro,
    apelidoDoBot,
    temApelido,
  ]);

  // ── Título central (tlTitle/tlSub/tlRight por nível) ───────────────────────
  const titulo = useMemo(() => {
    if (nivel === 'nano' && botSel) {
      return {
        principal: apelidoDoBot(botSel.bot),
        principalTitle: temApelido(botSel.bot) ? botSel.bot : undefined,
        aoVivo: Boolean(execId),
        // O FUNIL em uma linha: "6 entraram · 3 chegaram ao final · 3 pararam no
        // caminho". Responde direto ao "quero saber quantas pararam no meio do
        // caminho" — cada parcela vem da sua própria contagem, nunca de subtração.
        sub:
          resumoDoFunil(microDoBotSelecionado(botSel.bot) ?? undefined) ||
          'Etapas internas do bot, em tempo real.',
        direita: execDetalhe?.total
          ? `${execDetalhe.done} de ${execDetalhe.total} empresas concluídas neste bot`
          : '',
      };
    }
    if (nivel === 'board') {
      return {
        principal: empresaFiltradaNome ?? `${nomeGrupoSel} · linhas de dependência`,
        principalTitle: undefined,
        aoVivo: false,
        sub: empresaFiltradaNome
          ? 'Bots por onde esta empresa passa, em paralelo.'
          : 'Cada linha é uma cadeia de dependência real entre bots — sem dependência, correm em paralelo.',
        direita: '',
      };
    }
    return {
      principal: empresaFiltradaNome ?? 'Todos os grupos · linhas de dependência',
      principalTitle: undefined,
      aoVivo: false,
      sub: empresaFiltradaNome
        ? 'Grupos de bot por onde esta empresa passa, em paralelo.'
        : 'Cada linha é uma cadeia de dependência real entre grupos — sem dependência, correm em paralelo.',
      direita: '',
    };
  }, [
    nivel,
    botSel,
    execId,
    execDetalhe,
    nomeGrupoSel,
    empresaFiltradaNome,
    apelidoDoBot,
    temApelido,
  ]);

  const carregandoBoard =
    (grupoRealSel !== null && boardLoading) || (ehSentinela && vinculosLoading);

  /**
   * TRILHA DE CARTÕES (13/10) — os cartões de um fluxo, numerados e ligados por
   * um fio, como linha do tempo.
   *
   * `rolagem`: uma linha só, com barra de rolagem horizontal — pedido do dono:
   * *"e se fizermos uma barra de rolagem... ao invés de ficar esse tanto de
   * card"*. Seis etapas do Lucro Presumido ocupam a altura de uma.
   * `grade`: quebra de linha, para o modal de foco mostrar o fluxo inteiro.
   */
  const trilhaDeCartoes = (
    chave: string,
    nos: NoDados[],
    rotulo: string,
    modo: 'rolagem' | 'grade',
  ) => (
    <div
      data-tour={modo === 'rolagem' ? 'trilha' : undefined}
      style={{
        display: 'flex',
        flexWrap: modo === 'grade' ? 'wrap' : 'nowrap',
        alignItems: 'stretch',
        gap: `26px ${VAO_ENTRE_CARTOES}px`,
        ...(modo === 'rolagem'
          ? {
              overflowX: 'auto',
              scrollSnapType: 'x proximity',
              scrollbarWidth: 'thin',
              // Folga para a sombra dos cartões não ser cortada pelo overflow.
              padding: '6px 6px 16px',
              margin: '0 -6px',
            }
          : { padding: '6px' }),
      }}
    >
      {nos.map((n, i) => (
        <div
          key={`${chave}-${n.label}`}
          style={{
            flex: modo === 'grade' ? '1 1 290px' : '0 0 clamp(280px, 21vw, 340px)',
            maxWidth: modo === 'grade' ? '420px' : undefined,
            minWidth: 0,
            scrollSnapAlign: 'start',
          }}
        >
          <NoBoard dados={n} variante="linha" conectado={i > 0} numero={`${rotulo} ${i + 1}`} />
        </div>
      ))}
    </div>
  );

  // ── Linha do board com régua e numeração (template linhas 124-150) ─────────
  const linhaDoBoard = (
    chave: string,
    num: string,
    tituloLinha: string | null,
    nos: NoDados[],
    /** Área da linha (macro): alimenta o "X %" ao lado do nome. */
    area?: IAreaCobertura,
    /** Setas de reordenar o fluxo; `null` na ponta (não há para onde mover). */
    mover?: { subir: (() => void) | null; descer: (() => void) | null; focar?: () => void },
    /** Botão de Excel do fluxo (só na macro). */
    extra?: React.ReactNode,
  ) => (
    <React.Fragment key={chave}>
      {tituloLinha && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '-8px',
          }}
        >
          <div
            data-tour={mover?.focar ? 'fluxo' : undefined}
            // Clicar no nome abre o fluxo em FOCO (13/10, pedido do dono).
            onClick={mover?.focar}
            title={mover?.focar ? 'Abrir só este fluxo, em tela cheia' : undefined}
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: 'oklch(0.28 0.09 145)',
              cursor: mover?.focar ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {tituloLinha}
            {mover?.focar && (
              <span style={{ fontSize: '12px', fontWeight: 500, opacity: 0.6 }}>⤢ focar</span>
            )}
          </div>
          {/*
            O "X %" que o dono escreveu à mão ao lado do nome da área.

            É o NÍVEL MACRO: empresas distintas que passaram por ao menos um bot
            da área, sobre as empresas vigentes do REGIME da área. Clicável — abre
            a lista de onde o número saiu (mesmo array, regra de ouro).
          */}
          {area && (
            <span
              onClick={() =>
                abrirFatiaCobertura(
                  `Cobertura de ${area.nome}`,
                  area.cobertura,
                  `${area.nome} (área)`,
                )
              }
              title={tituloDaBolha(area.cobertura, `${area.nome} (área)`)}
              style={{
                fontFamily: FONTE_MONO,
                fontSize: '12px',
                fontWeight: 600,
                color: area.cobertura.percentual === null ? 'oklch(0.55 0.015 165)' : C.greenFg,
                background:
                  area.cobertura.percentual === null ? 'oklch(0.96 0.006 160)' : C.greenBg,
                border: `1px solid ${
                  area.cobertura.percentual === null ? 'oklch(0.88 0.008 160)' : C.green
                }`,
                borderRadius: '10px',
                padding: '2px 9px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {rotuloDaArea(area)}
            </span>
          )}
          {extra}
          {/* Ressalvas do denominador — nunca escondidas: elas dizem que o
              denominador não é o óbvio, e omiti-las faria ler um número certo
              como se fosse outro. */}
          {ressalvasDaArea(area).map(nota => (
            <span
              key={nota}
              title={nota}
              style={{
                fontSize: '11px',
                color: 'oklch(0.55 0.015 165)',
                textWrap: 'pretty',
              }}
            >
              ⚠ {nota}
            </span>
          ))}
          {mover && (
            <span
              data-tour="ordem"
              style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}
            >
              {(
                [
                  ['▲', 'Subir este fluxo', mover.subir],
                  ['▼', 'Descer este fluxo', mover.descer],
                ] as const
              ).map(([seta, dica, acao]) => (
                <button
                  key={seta}
                  type="button"
                  title={dica}
                  aria-label={`${dica}: ${tituloLinha}`}
                  disabled={!acao}
                  onClick={acao ?? undefined}
                  style={{
                    ...VIDRO,
                    width: '30px',
                    height: '30px',
                    borderRadius: '10px',
                    font: 'inherit',
                    fontSize: '11px',
                    color: 'oklch(0.35 0.05 165)',
                    cursor: acao ? 'pointer' : 'default',
                    opacity: acao ? 1 : 0.35,
                  }}
                >
                  {seta}
                </button>
              ))}
            </span>
          )}
        </div>
      )}
      {nos.some(n => n.parcelas) ? (
        trilhaDeCartoes(chave, nos, tituloLinha ? 'Etapa' : 'RPA', 'rolagem')
      ) : (
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '10px' }}>
          <div
            style={{
              flex: 'none',
              width: '22px',
              paddingTop: '14px',
              fontFamily: FONTE_MONO,
              fontSize: '10px',
              color: 'oklch(0.6 0.015 165)',
              textAlign: 'center',
            }}
          >
            {num}
          </div>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              gap: '4px',
              flex: 1,
              minWidth: 0,
              maxWidth: `${nos.length * 228}px`,
            }}
          >
            {nos.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  // 66px, não 36px: desde 17/09 a variante 'linha' também reserva o
                  // slot da bolha de porcentagem acima do círculo (22px + 8px de
                  // gap), então o centro do círculo desceu 30px. A régua tem que
                  // acompanhar, senão ela corta os nós no meio.
                  top: '66px',
                  left: `${50 / nos.length}%`,
                  right: `${50 / nos.length}%`,
                  height: '1px',
                  background: 'oklch(0.85 0.03 162)',
                }}
              />
            )}
            {nos.map(n => (
              <NoBoard key={`${chave}-${n.label}`} dados={n} variante="linha" />
            ))}
          </div>
        </div>
      )}
    </React.Fragment>
  );

  /**
   * Os passos do tour. As ações `antes` são o que faz "as telas irem abrindo":
   * o popup abre clicando no primeiro cartão de verdade (dado real, não mock) e
   * o foco abre no primeiro fluxo da ordem atual.
   */
  const fecharTelasDoTour = () => {
    setPopup(null);
    setFluxoFocado(null);
  };
  const abrirPrimeiroCartao = () => {
    setFluxoFocado(null);
    document.querySelector<HTMLElement>('[data-tour="cartao"]')?.click();
  };
  const passosDoTour: PassoDoTour[] = [
    {
      titulo: 'Como ler a aba Processos',
      texto:
        'Cada faixa é um fluxo (Fiscal Simples, Fiscal Presumido, Contábil...). Dentro dela, as etapas aparecem na ordem em que acontecem, ligadas por um fio. Vou te mostrar cada parte e abrir as telas no caminho. Use as setas do teclado ou os botões.',
      antes: fecharTelasDoTour,
    },
    {
      alvo: 'legenda',
      titulo: 'De onde vêm os números',
      texto:
        'Total é a quantidade de empresas ativas do regime com contrato vigente (o "cliente até" não venceu). Movimento é ter nota de saída ou de serviço na competência indicada. A etapa só fica verde com 100% das empresas concluídas, com e sem movimento.',
      antes: fecharTelasDoTour,
    },
    {
      alvo: 'fluxo',
      titulo: 'Nome do fluxo: clique para focar',
      texto:
        'Clicar no nome abre só este fluxo em tela cheia, com todas as etapas visíveis de uma vez. Bom para analisar um regime sem distração.',
    },
    {
      alvo: 'ordem',
      titulo: 'Reordenar os fluxos',
      texto:
        'As setas sobem ou descem o fluxo. O padrão é Fiscal no topo e Contábil abaixo. A ordem é global: quem tem perfil administrador grava e todo mundo passa a ver igual.',
    },
    {
      alvo: 'trilha',
      titulo: 'A linha do tempo das etapas',
      texto:
        'As etapas são numeradas (Etapa 1, 2, 3...) e ligadas por um fio. Quando não cabem na tela, role para o lado.',
    },
    {
      alvo: 'cartao',
      titulo: 'O cartão da etapa',
      texto:
        'Mostra o nome, quantos RPAs estão vinculados e o total de empresas do regime. Clicar em qualquer lugar do cartão abre a análise completa da etapa.',
    },
    {
      alvo: 'circulo',
      titulo: 'O círculo: quanto já foi',
      texto:
        'É o percentual de empresas concluídas sobre o total do regime. A cor acompanha só esse número: vermelho abaixo de 40%, amarelo de 40% a 74,99%, azul de 75% a 99,99% e verde com ✓ só em 100%. Cinza quando ninguém rodou.',
    },
    {
      alvo: 'parcelas',
      titulo: 'As parcelas: toda empresa em um lugar só',
      texto:
        'Concluídas, erro ou alerta, não constam no fluxo e sem movimento e sem registro somam exatamente o total. "Não constam" inclui quem só tem log de pendente. Depois vêm os dois grupos, com e sem movimento, cada um com suas concluídas, erros e as que não passaram. A última linha diz, das concluídas na etapa anterior, quantas já chegaram aqui. Cada linha é clicável e abre aquela lista.',
    },
    {
      alvo: 'popup',
      titulo: 'A análise da etapa',
      texto:
        'Este é o painel que abre ao clicar numa etapa. Ele ocupa a tela para caber tudo: parcelas, ranking de erros e a lista de empresas.',
      antes: abrirPrimeiroCartao,
    },
    {
      alvo: 'popup-abas',
      titulo: 'Uma aba por RPA',
      texto:
        'Quando a etapa tem mais de um RPA (como a Transmissão), cada um ganha uma aba com seus próprios ok, erro e o que falta. "Etapa inteira" junta todos: basta um erro em um RPA para a empresa contar como erro, e o painel avisa quantos são erro parcial.',
    },
    {
      alvo: 'popup-cartoes',
      titulo: 'Cartões que filtram a lista',
      texto:
        'Clique em Concluídas, Erro, Não constam ou Sem movimento para ver só aquelas empresas. Clique de novo para voltar a todas. Os percentuais são sobre o total do regime.',
    },
    {
      alvo: 'popup-top-erros',
      titulo: 'Top erros',
      texto:
        'Os erros mais frequentes da etapa, agrupados pela mensagem. Clicar em um deles mostra só as empresas com aquele erro. É por aqui que se ataca o maior problema primeiro.',
    },
    {
      alvo: 'popup-lista',
      titulo: 'A lista de empresas',
      texto:
        'Cada linha traz a mensagem do erro, "ver histórico" (a linha do tempo das execuções daquela empresa no RPA) e "ver print" (a tela capturada no momento do erro). O botão no rodapé leva aos RPAs da etapa.',
    },
    {
      alvo: 'foco',
      titulo: 'O fluxo em foco',
      texto:
        'Assim fica o fluxo aberto pelo nome: todas as etapas na tela, sem rolagem lateral. Os cartões funcionam igual, e a análise abre por cima.',
      antes: () => {
        setPopup(null);
        setFluxoFocado(fluxosNaOrdem[0] ?? null);
      },
    },
    {
      titulo: 'Pronto',
      texto:
        'Roteiro sugerido: olhe o círculo de cada etapa, abra a que não está verde, comece pelo Top erros e confira quem não consta no fluxo. O "?" ao lado do botão de atualizar reabre este tour quando quiser.',
      antes: fecharTelasDoTour,
    },
  ];

  /** Fundo colorido e suave atrás dos cartões — é o que o vidro desfoca. */
  const FUNDO_DO_BOARD: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    padding: 'clamp(16px, 2.2vw, 36px)',
    borderRadius: '26px',
    background:
      'radial-gradient(900px 420px at 0% 0%, oklch(0.9 0.09 165 / 0.75), transparent 62%), ' +
      'radial-gradient(820px 460px at 100% 8%, oklch(0.9 0.07 235 / 0.65), transparent 60%), ' +
      'radial-gradient(900px 520px at 55% 105%, oklch(0.93 0.08 95 / 0.55), transparent 62%), ' +
      'oklch(0.972 0.01 165)',
  };

  /** Faixa que diz DE ONDE vêm o total e o movimento — as duas perguntas do dono. */
  const legendaDoPainel = (
    <div
      data-tour="legenda"
      style={{
        ...VIDRO,
        borderRadius: '16px',
        padding: '12px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 22px',
        fontSize: '12px',
        color: 'oklch(0.4 0.02 165)',
      }}
    >
      <span>
        <b>Total</b> = empresas ativas do regime com contrato vigente (&quot;cliente até&quot; não
        vencido)
      </span>
      <span>
        <b>Movimento</b> = nota de saída ou serviço em {rotuloMovimento || '—'}
        {movimento ? ` · ${movimento.total_com_movimento} empresas com movimento` : ' · carregando'}
      </span>
      <span>
        <b>Cor do círculo</b> (% concluídas): vermelho abaixo de 40% · amarelo de 40% a 74,99% ·
        azul de 75% a 99,99% · <b>verde só com 100%</b>, com e sem movimento
      </span>
      {avisoOrdem && (
        <span role="alert" style={{ flexBasis: '100%', color: C.amberFg, fontWeight: 600 }}>
          ⚠ {avisoOrdem}
        </span>
      )}
    </div>
  );

  return (
    <>
      <div
        style={{
          background: 'oklch(1 0 0)',
          border: '1px solid oklch(0.92 0.006 160)',
          borderRadius: '12px',
          padding: '20px 22px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              {crumbs.map(c => (
                <div
                  key={c.label}
                  onClick={c.onSelect}
                  title={c.title}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    padding: '7px 14px',
                    borderRadius: '20px',
                    border: `1px solid ${c.border}`,
                    background: c.bg,
                    color: c.color,
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontFamily: FONTE_MONO, fontSize: '11px', color: c.numColor }}>
                    {c.num}
                  </span>
                  <span>{c.label}</span>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '8px',
                borderLeft: '1px solid oklch(0.92 0.006 160)',
              }}
            >
              <span
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '10px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'oklch(0.6 0.015 165)',
                  whiteSpace: 'nowrap',
                }}
              >
                Regime
              </span>
              <SelectFiltro
                value={filtros.regime ?? ''}
                onChange={novo => aplicarFiltros(aoEscolherRegime(filtros, novo || null))}
                opcoes={[
                  { value: '', label: 'Todos os regimes' },
                  ...regimes.map(r => ({ value: r, label: r })),
                ]}
                selectStyle={{ padding: '7px 32px 7px 12px', maxWidth: '180px' }}
                chevronStyle={{ right: '12px', fontSize: '11px' }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '8px',
                borderLeft: '1px solid oklch(0.92 0.006 160)',
              }}
            >
              <span
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '10px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'oklch(0.6 0.015 165)',
                  whiteSpace: 'nowrap',
                }}
              >
                Empresa
              </span>
              {/*
                BUSCA por id OU razão social (pedido 17/09) — substitui o `<select>`
                nativo. O select só casava o PREFIXO do label, e o label é
                "Nome_1117": digitar o código nunca achava nada, porque ele está no
                fim do texto. Com ~1,6 mil empresas isso obrigava a rolar a lista.

                O que a escolha FAZ não mudou: o mesmo `aoEscolherEmpresa` com o
                mesmo auto-preenchimento de regime. Trocou-se a forma de escolher,
                não o filtro.
              */}
              {/*
                ID ou RAZÃO SOCIAL (19/10: "a forma de filtrar empresas igual
                colocamos nas demais"), com ID como padrão. Os dois modos chegam ao
                MESMO `aoEscolherEmpresa`: o ID aplica assim que o código digitado
                existe na lista; apagar o campo limpa o filtro.
              */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  borderRadius: '10px',
                  border: '1px solid oklch(0.9 0.006 160)',
                  background: 'oklch(1 0 0)',
                }}
              >
                <SelectFiltro
                  value={modoEmpresa}
                  onChange={novo => {
                    setModoEmpresa(novo === 'razao' ? 'razao' : 'id');
                    setCodigoDigitado('');
                    if (filtros.empresa) aplicarFiltros(aoEscolherEmpresa(filtros, null, null));
                  }}
                  opcoes={[
                    { value: 'id', label: 'ID' },
                    { value: 'razao', label: 'Razão social' },
                  ]}
                  wrapperStyle={{ borderRight: '1px solid oklch(0.9 0.006 160)' }}
                  selectStyle={{
                    padding: '7px 28px 7px 10px',
                    border: 'none',
                    borderRadius: '10px 0 0 10px',
                    background: 'transparent',
                    fontWeight: 600,
                  }}
                />
                {modoEmpresa === 'id' ? (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px' }}
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Cód. empresa"
                      value={codigoDigitado}
                      onChange={ev => {
                        const texto = ev.target.value.trim();
                        setCodigoDigitado(ev.target.value);
                        if (!texto) {
                          if (filtros.empresa) {
                            aplicarFiltros(aoEscolherEmpresa(filtros, null, null));
                          }
                          return;
                        }
                        const achada = empresas.find(emp => emp.codi_emp === texto);
                        if (achada && filtros.empresa !== texto) {
                          aplicarFiltros(
                            aoEscolherEmpresa(filtros, texto, achada.empresa?.regime ?? null),
                          );
                        }
                      }}
                      style={{
                        width: '92px',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontFamily: FONTE_MONO,
                        fontSize: '12px',
                        color: 'oklch(0.3 0.02 165)',
                      }}
                    />
                    {codigoDigitado.trim() &&
                      !empresas.some(emp => emp.codi_emp === codigoDigitado.trim()) && (
                        <span style={{ fontSize: '11px', color: C.amberFg, whiteSpace: 'nowrap' }}>
                          não encontrada
                        </span>
                      )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                    <BuscaEmpresa
                      opcoes={opcoesBuscaEmpresa}
                      valor={filtros.empresa}
                      onEscolher={codi => {
                        const regimeDaEmpresa =
                          empresas.find(emp => emp.codi_emp === codi)?.empresa?.regime ?? null;
                        aplicarFiltros(aoEscolherEmpresa(filtros, codi, regimeDaEmpresa));
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '8px',
                borderLeft: '1px solid oklch(0.92 0.006 160)',
              }}
            >
              <span
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '10px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'oklch(0.6 0.015 165)',
                  whiteSpace: 'nowrap',
                }}
              >
                Status
              </span>
              <FiltroStatus
                selecionados={filtros.status}
                onToggle={alternarStatus}
                onLimpar={limparStatus}
                macro={nivel === 'macro'}
              />
            </div>
            {/* LIMPAR FILTROS (24/09): um clique para voltar ao estado sem
                recorte. Antes so' dava para limpar campo a campo — e o filtro de
                empresa, que e' um combobox, nao tinha nem o "✕" que os outros
                tinham. O PERIODO nao entra: ele sempre tem valor (30 dias e' o
                default, nao uma escolha), e zera-lo junto faria quem so' queria
                tirar o recorte de empresa perder a janela que estava olhando. */}
            {temFiltroAtivo(filtros) && (
              <button
                type="button"
                onClick={() => aplicarFiltros(limparFiltros(filtros))}
                title="Remover regime, empresa e status (o período é preservado)"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 10px',
                  marginLeft: '4px',
                  borderRadius: '8px',
                  border: '1px solid oklch(0.88 0.008 160)',
                  background: 'oklch(1 0 0)',
                  color: 'oklch(0.45 0.015 165)',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                ✕ Limpar filtros
              </button>
            )}
            {/* Chip discreto (M3 08/09): torna VISÍVEL o recorte por regime
                configurado na aba Dependências — sem ele, nós/contagens mudavam
                "sozinhos" sem nenhum filtro aparente. O title lista os níveis. */}
            {regimePorNivelAtivo && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '8px',
                  borderLeft: '1px solid oklch(0.92 0.006 160)',
                }}
              >
                <div
                  title={`Regime configurado em:\n${niveisComRegime.join('\n')}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 10px',
                    borderRadius: '20px',
                    border: '1px dashed oklch(0.84 0.04 162)',
                    background: 'oklch(0.97 0.008 160)',
                    color: 'oklch(0.5 0.09 165)',
                    fontFamily: FONTE_MONO,
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'help',
                  }}
                >
                  <span
                    style={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      background: 'oklch(0.65 0.15 162)',
                      flex: 'none',
                    }}
                  />
                  Recorte por regime ativo
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              textAlign: 'center',
              maxWidth: '720px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <div
                title={titulo.principalTitle}
                style={{ fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em' }}
              >
                {titulo.principal}
              </div>
              {titulo.aoVivo && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 9px',
                    borderRadius: '20px',
                    background: 'oklch(0.94 0.04 162)',
                    color: 'oklch(0.4 0.1 165)',
                    fontFamily: FONTE_MONO,
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: 'oklch(0.62 0.15 162)',
                    }}
                  />
                  Ao vivo
                </div>
              )}
            </div>
            <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)', textWrap: 'pretty' }}>
              {titulo.sub}
            </div>
            <div
              style={{ fontFamily: FONTE_MONO, fontSize: '11px', color: 'oklch(0.5 0.015 165)' }}
            >
              {titulo.direita}
            </div>
          </div>
        </div>

        {nivel === 'macro' && (
          <div style={FUNDO_DO_BOARD}>
            {legendaDoPainel}
            {linhasMacroOrdenadas.map((linha, li) => {
              const chaveFluxo = linha.departamento ?? '';
              // Posição entre os MOVÍVEIS: -1 = fluxo sem departamento cadastrado
              // ("Bots sem grupo"), que não tem onde guardar ordem e fica fixo.
              const posicao = fluxosMoviveis.indexOf(chaveFluxo);
              // A área da linha é o DEPARTAMENTO do primeiro grupo (§3.3) — a
              // mesma chave que `/esteira` usa como título da linha. Linha sem
              // departamento não tem área e fica sem percentual (em vez de
              // receber o de outra área qualquer).
              const area = linha.departamento ? areasPorNome.get(linha.departamento) : undefined;
              // A coerência área↔nó é reconferida NA TELA antes de desenhar: se a
              // área cobrir MENOS que um dos seus bots, a união é menor que a
              // parte — impossível — e o percentual é suprimido em vez de exibido.
              const areaOk = area && areaCoerente(area) ? area : undefined;
              return linhaDoBoard(
                `linha-grupo-${li}-${linha.grupos[0]?.id ?? SEM_GRUPO_ID}`,
                String(li + 1),
                linha.departamento ?? 'Sem departamento vinculado',
                linha.grupos.map((g, gi) =>
                  noDoGrupo(g, gi > 0 ? linha.grupos[gi - 1] : undefined, areaOk),
                ),
                areaOk,
                {
                  subir: posicao > 0 ? () => moverFluxo(chaveFluxo, -1) : null,
                  descer:
                    posicao >= 0 && posicao < fluxosMoviveis.length - 1
                      ? () => moverFluxo(chaveFluxo, 1)
                      : null,
                  focar: () => setFluxoFocado(chaveFluxo),
                },
                linha.departamento ? botaoExcel(linha.departamento, linha.grupos) : undefined,
              );
            })}
            {!esteiraLoading && !resumoLoading && !linhasMacro.length && (
              <EstadoVazio>
                {/* Só o filtro global esvazia por pruning (M2) — regime sozinho não. */}
                {filtroAtivo
                  ? 'Nenhum grupo com movimento no recorte selecionado.'
                  : 'Nenhum grupo cadastrado ainda — organize os bots na aba Dependências.'}
              </EstadoVazio>
            )}
          </div>
        )}

        {nivel === 'board' && (
          <div style={FUNDO_DO_BOARD}>
            {areaDoBoard && legendaDoPainel}
            {linhasBots.map((linha, li) =>
              linhaDoBoard(
                `linha-${li}-${linha[0]?.bot ?? 'vazia'}`,
                String(li + 1),
                null,
                linha.map((b, bi) => noDoBot(b, bi > 0 ? linha[bi - 1] : undefined)),
              ),
            )}
            {!carregandoBoard && !linhasBots.length && (
              <EstadoVazio>
                {/* Só o filtro global esvazia por pruning (M2) — regime sozinho não. */}
                {filtroAtivo
                  ? 'Nenhum bot deste grupo passa pelo recorte selecionado.'
                  : 'Nenhum bot neste grupo.'}
              </EstadoVazio>
            )}
          </div>
        )}

        {nivel === 'nano' && execId && modulosNano.length > 0 && (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: '4px' }}>
            <div
              style={{
                position: 'absolute',
                // 66px (e não 36px): o slot da bolinha "já passaram" desce o centro
                // do círculo em 30px (ver NoBoard, variante nano).
                top: '66px',
                // A régua liga só os módulos da trilha — o balde "Etapa não
                // informada" (quando existir) fica fora dela de propósito: ele não
                // é uma etapa do catálogo, é posição não informada pelo bot.
                left: `${50 / (modulosNano.length + (bucketEmVoo > 0 ? 1 : 0))}%`,
                right: `${(bucketEmVoo > 0 ? 150 : 50) / (modulosNano.length + (bucketEmVoo > 0 ? 1 : 0))}%`,
                height: '1px',
                background: 'oklch(0.9 0.008 160)',
              }}
            />
            {modulosNano.map((m, i) => (
              <NoBoard key={m.modulo_id} dados={noDoModulo(m, i)} variante="nano" />
            ))}
            {bucketEmVoo > 0 && <NoBoard key="sem-etapa" dados={noSemEtapa()} variante="nano" />}
          </div>
        )}
        {nivel === 'nano' && !execId && (
          <EstadoVazio>
            Sem execução ativa para{' '}
            <span title={botSel && temApelido(botSel.bot) ? botSel.bot : undefined}>
              {botSel ? apelidoDoBot(botSel.bot) : ''}
            </span>{' '}
            agora. As etapas internas (nível nano) são lidas da trilha de módulos de uma execução —
            quando o bot iniciar a próxima, elas aparecem aqui ao vivo.
          </EstadoVazio>
        )}
        {nivel === 'nano' && execId && !nanoLoading && !modulosNano.length && (
          <EstadoVazio>
            Esta execução não declarou módulos no catálogo — o nível nano fica disponível quando o
            bot publicar a trilha de etapas.
          </EstadoVazio>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(560px, 1fr))',
          gap: '16px',
          alignItems: 'start',
        }}
      >
        {nivel === 'nano' && grupoEtapas.length > 0 && (
          <ListaLateral
            title="Etapas do bot"
            sub="Clique numa etapa para ver quais empresas estão nela."
            groups={grupoEtapas}
            m1Label="Duração média"
            m2Label="Empresas"
            m3Label="Com erro"
          />
        )}
        {/* O card "Fila" saiu da macro e do board (13/10, pedido do dono: "consegue
            tirar ele? aí você ganha mais espaço na tela"). As listas dele agora
            abrem no popup de cada etapa. Fica só no nano, onde ele é a fila da
            EXECUÇÃO ao vivo, que nenhum outro lugar da tela mostra. */}
        {nivel === 'nano' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
            <FilaCard dados={filaDados} />
          </div>
        )}
      </div>

      {/* BOTÃO "?" — abre o tour guiado (13/10). Desenhado por PORTAL no
          cabeçalho da página, ao lado do "Atualizar agora" (pedido do dono: "fica
          melhor do que ele flutuando ali"). O encaixe é da página; o tour é desta
          aba, que conhece as telas que ele abre. Sem o encaixe (página montada
          sem o cabeçalho) o botão simplesmente não aparece. */}
      {nivel === 'macro' &&
        slotDoTour &&
        createPortal(
          <button
            type="button"
            onClick={() => setTourAberto(true)}
            title="Tour guiado: como ler e operar esta tela"
            aria-label="Abrir o tour guiado da aba Processos"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid oklch(0.88 0.03 162)',
              background: 'oklch(1 0 0)',
              font: 'inherit',
              fontSize: '15px',
              fontWeight: 700,
              color: 'oklch(0.32 0.08 165)',
              cursor: 'pointer',
              flex: 'none',
            }}
          >
            ?
          </button>,
          slotDoTour,
        )}
      {tourAberto && (
        <TourGuiado
          passos={passosDoTour}
          onClose={() => {
            setTourAberto(false);
            setPopup(null);
            setFluxoFocado(null);
          }}
        />
      )}

      {/* FLUXO EM FOCO (13/10, pedido do dono: "eu clico em cima do nome, ele abre
          um modal com o fluxo todo pra eu focar só nesse fluxo"). As etapas
          quebram de linha para caber o máximo na tela; se não couber, rola. O
          popup da etapa abre POR CIMA (zIndex maior), então dá para analisar
          tudo sem sair do foco. */}
      {nivel === 'macro' && fluxoFocado !== null && (
        <div
          onClick={() => setFluxoFocado(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            background: 'oklch(0.2 0.02 165 / 0.45)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(10px, 2vw, 28px)',
          }}
        >
          <div
            data-tour="foco"
            onClick={ev => ev.stopPropagation()}
            style={{
              ...FUNDO_DO_BOARD,
              gap: '20px',
              width: '100%',
              maxWidth: 'min(1800px, 97vw)',
              maxHeight: '94vh',
              overflowY: 'auto',
              boxSizing: 'border-box',
              boxShadow: '0 30px 90px oklch(0.2 0.02 165 / 0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'oklch(0.28 0.09 145)' }}>
                  {fluxoFocado || 'Sem departamento vinculado'}
                </div>
                {fluxoFocado &&
                  botaoExcel(
                    fluxoFocado,
                    linhasMacroOrdenadas
                      .filter(l => (l.departamento ?? '') === fluxoFocado)
                      .flatMap(l => l.grupos),
                  )}
              </div>
              <button
                type="button"
                onClick={() => setFluxoFocado(null)}
                aria-label="Fechar o foco do fluxo"
                style={{
                  ...VIDRO,
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  font: 'inherit',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: 'oklch(0.4 0.02 165)',
                }}
              >
                ✕
              </button>
            </div>
            {linhasMacroOrdenadas
              .filter(linha => (linha.departamento ?? '') === fluxoFocado)
              .map((linha, li) => {
                const area = linha.departamento ? areasPorNome.get(linha.departamento) : undefined;
                const areaOk = area && areaCoerente(area) ? area : undefined;
                const nos = linha.grupos.map((g, gi) =>
                  noDoGrupo(g, gi > 0 ? linha.grupos[gi - 1] : undefined, areaOk),
                );
                return nos.some(n => n.parcelas) ? (
                  <React.Fragment key={`foco-${linha.grupos[0]?.id ?? li}`}>
                    {trilhaDeCartoes(`foco-${li}`, nos, 'Etapa', 'grade')}
                  </React.Fragment>
                ) : (
                  <EstadoVazio key={`foco-vazio-${linha.grupos[0]?.id ?? li}`}>
                    Este fluxo ainda não tem total de empresas conhecido para montar o painel.
                  </EstadoVazio>
                );
              })}
          </div>
        </div>
      )}

      {/* TELA DE ESPERA DO EXCEL: cobre a página para ninguém trocar de aba no
          meio (perde o arquivo). Fechar/recarregar cai no aviso do navegador. */}
      {exportando !== null && (
        <div
          role="alertdialog"
          aria-live="polite"
          aria-label={`Gerando a planilha de ${exportando}`}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: 'oklch(0.2 0.02 165 / 0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          <div
            style={{
              ...VIDRO,
              background: 'oklch(1 0 0 / 0.92)',
              borderRadius: '22px',
              padding: '30px 34px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span className="orq-girar orq-girar-grande" aria-hidden="true" />
            <div style={{ fontSize: '17px', fontWeight: 700, color: 'oklch(0.28 0.09 145)' }}>
              Gerando a planilha
            </div>
            <div style={{ fontSize: '13px', color: 'oklch(0.45 0.02 165)', lineHeight: 1.5 }}>
              {exportando}: montando o resumo das etapas, os erros e a situação de cada empresa.
            </div>
            <div
              style={{
                fontFamily: FONTE_MONO,
                fontSize: '22px',
                fontWeight: 700,
                color: 'oklch(0.45 0.13 150)',
              }}
            >
              {Math.floor(segundosExportando / 60)}:
              {String(segundosExportando % 60).padStart(2, '0')}
            </div>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: C.amberFg }}>
              Não feche nem saia desta tela até o download começar.
            </div>
          </div>
        </div>
      )}
      {avisoExportacao && (
        <button
          type="button"
          aria-live="polite"
          title="Fechar o aviso"
          onClick={() => setAvisoExportacao(null)}
          style={{
            ...VIDRO,
            font: 'inherit',
            textAlign: 'left',
            position: 'fixed',
            right: '20px',
            bottom: '20px',
            zIndex: 2000,
            maxWidth: '420px',
            padding: '12px 16px',
            borderRadius: '14px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            background: avisoExportacao.ok
              ? 'oklch(0.97 0.03 150 / 0.95)'
              : 'oklch(0.97 0.03 25 / 0.95)',
            color: avisoExportacao.ok ? C.greenFg : C.redFg,
          }}
        >
          {avisoExportacao.ok ? '✓ ' : '✕ '}
          {avisoExportacao.texto}
        </button>
      )}

      {/* `macro` aqui e nao em cada `setPopup`: sao 6 pontos que abrem popup, e
          esquecer um deixaria a mesma tela com dois vocabularios. O nivel e' do
          componente, nao do clique. */}
      <PopupProcessos
        dados={dadosDoPopup}
        onClose={() => setPopup(null)}
        onVerNaAuditoria={onVerNaAuditoria}
        apelidoDoBot={apelidoDoBot}
        // A MESMA janela que alimenta os números do nó (decisão do dono, 05/10):
        // o histórico da linha tem de falar do mesmo recorte, senão a linha do
        // tempo contradiz a bolha que a abriu. `params` é o `paraParams(periodo)`
        // que já alimenta as chamadas desta aba.
        periodo={{ de: params.date_init, ate: params.date_end }}
      />
    </>
  );
};
